/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, faEnvelope, faPhone, faCalendar, faMapMarkerAlt,
  faGraduationCap, faBriefcase, faRocket, faUpload, faCheck,
  faInfoCircle, faCamera, faGlobe, faPlus, faTimes,
  faPassport, faMapMarked, faBullseye, faLightbulb, faLanguage,
  faCertificate, faUsers, faUserCheck, faLink, faSlidersH,
  faBuilding, faCheckCircle, faSave, faEdit, faSearch
} from '@fortawesome/free-solid-svg-icons';
import { faLinkedin, faGithub, faMedium, faTwitter } from '@fortawesome/free-brands-svg-icons';
import { buildApiUrl } from '../config/api';
import { useAutoSave } from '../hooks/useAutoSave';
import AutoSaveStatus from '../components/AutoSaveStatus';
import '../styles/InternRegistrationForm.css';
import '../styles/InternRegistrationFormMobile.css';
import '../styles/InternRegistrationFormMobile_CRITICAL.css';
import '../styles/JobSeekerRegistrationFormMobile.css';
import globalSkills from '../data/global_skills.json';
import { softwareAndTools, industries } from '../data/formOptions';
import { ASSOCIATIONS, COMMUNITIES, ALL_OPTIONS, COUNTRIES as COMMUNITY_COUNTRIES } from '../data/communitiesAndAssociations';

import LocationMap from '../components/LocationMap';

// Community/Association Selector Component
const CommunityAssociationSelector = ({ value = [], onChange }) => {
  const [selectedItems, setSelectedItems] = useState(value);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [manualInput, setManualInput] = useState({ name: '', type: 'community', country: '' });
  const [showManualForm, setShowManualForm] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSelectedItems(value);
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddFromDropdown = (option) => {
    const newItem = {
      name: option.name,
      type: option.type,
      country: ''
    };
    if (!selectedItems.some(item => item.name === option.name && item.type === option.type)) {
      const updated = [...selectedItems, newItem];
      setSelectedItems(updated);
      onChange(updated);
    }
    setDropdownOpen(false);
    setSearchQuery('');
  };

  const handleAddManual = () => {
    if (manualInput.name.trim()) {
      const newItem = {
        name: manualInput.name.trim(),
        type: manualInput.type,
        country: manualInput.country
      };
      if (!selectedItems.some(item => item.name === newItem.name && item.type === newItem.type)) {
        const updated = [...selectedItems, newItem];
        setSelectedItems(updated);
        onChange(updated);
        setManualInput({ name: '', type: 'community', country: '' });
        setShowManualForm(false);
      }
    }
  };

  const handleRemove = (index) => {
    const updated = selectedItems.filter((_, i) => i !== index);
    setSelectedItems(updated);
    onChange(updated);
  };

  const handleUpdateCountry = (index, country) => {
    const updated = selectedItems.map((item, i) => 
      i === index ? { ...item, country } : item
    );
    setSelectedItems(updated);
    onChange(updated);
  };

  // Filter and group options
  const availableOptions = ALL_OPTIONS.filter(opt => 
    !selectedItems.some(item => item.name === opt.name && item.type === opt.type)
  );

  const filteredOptions = availableOptions.filter(opt =>
    opt.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const associations = filteredOptions.filter(opt => opt.type === 'association');
  const communities = filteredOptions.filter(opt => opt.type === 'community');

  return (
    <div style={{ width: '100%' }}>
      {/* Dropdown Selector with Search */}
      <div style={{ position: 'relative', marginBottom: '10px' }} ref={dropdownRef}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #ddd',
                borderRadius: '6px',
                background: 'white',
                cursor: 'pointer'
              }}
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <div style={{ padding: '8px 12px', color: '#666' }}>
                <FontAwesomeIcon icon={faSearch} />
              </div>
              <input
                type="text"
                placeholder="Search communities/associations..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setDropdownOpen(true);
                }}
                onFocus={() => setDropdownOpen(true)}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: 'none',
                  outline: 'none',
                  fontSize: '14px',
                  borderRadius: '6px'
                }}
              />
            </div>

            {/* Custom Dropdown */}
            {dropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: '6px',
                marginTop: '4px',
                maxHeight: '400px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}>
                {filteredOptions.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                    No results found
                  </div>
                ) : (
                  <>
                    {/* Associations Section */}
                    {associations.length > 0 && (
                      <div>
                        <div style={{
                          padding: '10px 15px',
                          background: '#f8f9fa',
                          borderBottom: '1px solid #e9ecef',
                          fontWeight: '600',
                          fontSize: '13px',
                          color: '#495057',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Associations
                        </div>
                        {associations.map((opt, idx) => (
                          <div
                            key={`assoc-${idx}`}
                            onClick={() => handleAddFromDropdown(opt)}
                            style={{
                              padding: '12px 15px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f0f0f0',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.background = 'white'}
                          >
                            {opt.name}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Communities Section */}
                    {communities.length > 0 && (
                      <div>
                        <div style={{
                          padding: '10px 15px',
                          background: '#f8f9fa',
                          borderTop: associations.length > 0 ? '2px solid #e9ecef' : 'none',
                          borderBottom: '1px solid #e9ecef',
                          fontWeight: '600',
                          fontSize: '13px',
                          color: '#495057',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Communities
                        </div>
                        {communities.map((opt, idx) => (
                          <div
                            key={`comm-${idx}`}
                            onClick={() => handleAddFromDropdown(opt)}
                            style={{
                              padding: '12px 15px',
                              cursor: 'pointer',
                              borderBottom: idx < communities.length - 1 ? '1px solid #f0f0f0' : 'none',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.background = 'white'}
                          >
                            {opt.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowManualForm(!showManualForm)}
            style={{
              padding: '10px 15px',
              background: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              whiteSpace: 'nowrap'
            }}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Manually
          </button>
        </div>
      </div>

      {/* Manual Entry Form */}
      {showManualForm && (
        <div style={{
          padding: '15px',
          border: '1px solid #ddd',
          borderRadius: '6px',
          marginBottom: '10px',
          background: '#f9f9f9'
        }}>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Name <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              value={manualInput.name}
              onChange={(e) => setManualInput({ ...manualInput, name: e.target.value })}
              placeholder="Enter community/association name"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Type</label>
              <select
                value={manualInput.type}
                onChange={(e) => setManualInput({ ...manualInput, type: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <option value="community">Community</option>
                <option value="association">Association</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Country</label>
              <select
                value={manualInput.country}
                onChange={(e) => setManualInput({ ...manualInput, country: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <option value="">Select country</option>
                {COMMUNITY_COUNTRIES.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={handleAddManual}
              disabled={!manualInput.name.trim()}
              style={{
                padding: '8px 15px',
                background: manualInput.name.trim() ? '#27ae60' : '#bdc3c7',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: manualInput.name.trim() ? 'pointer' : 'not-allowed'
              }}
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => {
                setShowManualForm(false);
                setManualInput({ name: '', type: 'community', country: '' });
              }}
              style={{
                padding: '8px 15px',
                background: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Selected Items Display */}
      {selectedItems.length > 0 && (
        <div style={{ marginTop: '15px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Selected Communities/Associations:
          </label>
          {selectedItems.map((item, index) => (
            <div
              key={index}
              style={{
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                marginBottom: '8px',
                background: 'white',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong>{item.name}</strong>
                  <span style={{
                    marginLeft: '10px',
                    padding: '2px 8px',
                    background: item.type === 'association' ? '#e3f2fd' : '#f3e5f5',
                    color: item.type === 'association' ? '#1976d2' : '#7b1fa2',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    {item.type === 'association' ? 'Association' : 'Community'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#e74c3c',
                    cursor: 'pointer',
                    fontSize: '16px',
                    padding: '4px 8px'
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                  Country/Location:
                </label>
                <select
                  value={item.country || ''}
                  onChange={(e) => handleUpdateCountry(index, e.target.value)}
                  style={{
                    width: '100%',
                    padding: '6px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '13px'
                  }}
                >
                  <option value="">Select country</option>
                  {COMMUNITY_COUNTRIES.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const JobSeekerRegistrationFormComprehensive = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateUser } = useAuth();
  const userData = location.state?.userData || {};
  const existingData = location.state?.existingData || null;
  // Map handled by LocationMap component

  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [userProfileData, setUserProfileData] = useState(null);
  
  // Auto-save configuration
  const AUTOSAVE_KEY = `jobseeker_registration_${user?.userId || 'temp'}`;
  
  // Fetch user profile data on mount - BACKEND ONLY (No localStorage conflicts)
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        console.log('🔄 Fetching user profile from backend (SINGLE SOURCE OF TRUTH)...');
        
        // Fetch from backend - This is the ONLY source of truth
        let response = await fetch(buildApiUrl('/api/jobseeker/profile'), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          // Fallback to general profile
          response = await fetch(buildApiUrl('/api/profile/profile'), {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
        }
        
        if (response.ok) {
          const backendData = await response.json();
          console.log('📋 Fetched backend profile data:', {
            fieldsCount: Object.keys(backendData).length,
            isDraft: backendData.isDraft,
            profileCompleted: backendData.profileCompleted,
            profileCompletion: backendData.profileCompletion,
            lastUpdated: backendData.updatedAt || backendData.lastUpdatedAt
          });
          
          // Use backend data as-is (no localStorage merge)
          setUserProfileData(backendData);
          console.log('✅ Backend data loaded - ready to populate form');
        } else {
          console.warn('⚠️  Failed to fetch backend profile, status:', response.status);
        }
      } catch (error) {
        console.error('❌ Error fetching user profile:', error);
      }
    };
    
    fetchUserProfile();
  }, [user?.userId]);
  
  // Countries list
  const countries = [
    "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", "Australia",
    "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium",
    "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei",
    "Bulgaria", "Burkina Faso", "Burundi", "Cambodia", "Cameroon", "Canada", "Cape Verde",
    "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica",
    "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic",
    "East Timor", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia",
    "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana",
    "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras",
    "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy",
    "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "North Korea", "South Korea",
    "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya",
    "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives",
    "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia",
    "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia",
    "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "Norway",
    "Oman", "Pakistan", "Palau", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines",
    "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis",
    "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Saudi Arabia",
    "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia",
    "Solomon Islands", "Somalia", "South Africa", "South Sudan", "Spain", "Sri Lanka", "Sudan",
    "Suriname", "Swaziland", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania",
    "Thailand", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan",
    "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States",
    "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam", "Yemen",
    "Zambia", "Zimbabwe"
  ];

  const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

  const NGOS = [
    "Amref Health Africa",
    "African Wildlife Foundation",
    "World Vision Kenya",
    "Oxfam Kenya",
    "Mercy Corps Kenya",
    "Save the Children Kenya",
    "CARE Kenya",
    "Plan International Kenya",
    "Kenya Red Cross Society",
    "Action Against Hunger Kenya",
    "Catholic Relief Services Kenya",
    "International Rescue Committee Kenya",
    "Doctors Without Borders (MSF) Kenya",
    "African Population and Health Research Center (APHRC)",
    "Kenya Human Rights Commission",
    "Federation of Women Lawyers – Kenya (FIDA Kenya)",
    "Transparency International Kenya",
    "Umande Trust",
    "Carolina for Kibera (CFK Africa)",
    "Horn of Africa Development Initiative (HODI)",
    "Green Belt Movement",
    "WildlifeDirect",
    "Handicap International Kenya",
    "Water.org Kenya",
    "Living Goods Kenya",
    "Elite Ability Foundation Africa (ELAFA)"
  ];

// NGO Selector Component with Search and Manual Entry
const NGOSelector = ({ value = '', onChange }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [manualInput, setManualInput] = useState('');
  const [showManualForm, setShowManualForm] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectNGO = (ngo) => {
    onChange(ngo);
    setDropdownOpen(false);
    setSearchQuery('');
  };

  const handleAddManual = () => {
    if (manualInput.trim()) {
      onChange(manualInput.trim());
      setManualInput('');
      setShowManualForm(false);
    }
  };

  const handleClear = () => {
    onChange('');
    setSearchQuery('');
  };

  // Filter NGOs based on search query
  const filteredNGOS = NGOS.filter(ngo =>
    ngo.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div style={{ width: '100%' }}>
      {/* Current Value Display */}
      {value && (
        <div style={{
          padding: '12px',
          border: '1px solid #ddd',
          borderRadius: '6px',
          marginBottom: '10px',
          background: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ fontWeight: '500' }}>{value}</span>
          <button
            type="button"
            onClick={handleClear}
            style={{
              background: 'none',
              border: 'none',
              color: '#e74c3c',
              cursor: 'pointer',
              fontSize: '16px',
              padding: '4px 8px'
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {/* Dropdown Selector with Search */}
      <div style={{ position: 'relative', marginBottom: '10px' }} ref={dropdownRef}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #ddd',
                borderRadius: '6px',
                background: 'white',
                cursor: 'pointer'
              }}
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <div style={{ padding: '8px 12px', color: '#666' }}>
                <FontAwesomeIcon icon={faSearch} />
              </div>
              <input
                type="text"
                placeholder="Search or type NGO name..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setDropdownOpen(true);
                }}
                onFocus={() => setDropdownOpen(true)}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: 'none',
                  outline: 'none',
                  fontSize: '14px',
                  borderRadius: '6px'
                }}
              />
            </div>

            {/* Custom Dropdown */}
            {dropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: '6px',
                marginTop: '4px',
                maxHeight: '300px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}>
                {filteredNGOS.length === 0 && !searchQuery ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                    No NGOs found
                  </div>
                ) : filteredNGOS.length === 0 && searchQuery ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                    No matching NGOs found. Use "Add Manually" to add "{searchQuery}"
                  </div>
                ) : (
                  filteredNGOS.map((ngo, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleSelectNGO(ngo)}
                      style={{
                        padding: '12px 15px',
                        cursor: 'pointer',
                        borderBottom: idx < filteredNGOS.length - 1 ? '1px solid #f0f0f0' : 'none',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                      onMouseLeave={(e) => e.target.style.background = 'white'}
                    >
                      {ngo}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              setShowManualForm(!showManualForm);
              setDropdownOpen(false);
            }}
            style={{
              padding: '10px 15px',
              background: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              whiteSpace: 'nowrap'
            }}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Manually
          </button>
        </div>
      </div>

      {/* Manual Entry Form */}
      {showManualForm && (
        <div style={{
          padding: '15px',
          border: '1px solid #ddd',
          borderRadius: '6px',
          marginBottom: '10px',
          background: '#f9f9f9'
        }}>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              NGO Name <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              value={manualInput}
              onChange={(e) => setManualInput(e.target.value)}
              placeholder="Enter NGO name"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && manualInput.trim()) {
                  handleAddManual();
                }
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={handleAddManual}
              disabled={!manualInput.trim()}
              style={{
                padding: '8px 15px',
                background: manualInput.trim() ? '#27ae60' : '#bdc3c7',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: manualInput.trim() ? 'pointer' : 'not-allowed'
              }}
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => {
                setShowManualForm(false);
                setManualInput('');
              }}
              style={{
                padding: '8px 15px',
                background: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

  // Backend save callback for auto-save hook
  const backendSaveCallback = React.useCallback(async (data) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('⚠️  No token found, skipping backend auto-save');
        return;
      }

      const formDataToSend = new FormData();
      
      // Append all fields to FormData
      Object.keys(data).forEach(key => {
        if (Array.isArray(data[key])) {
          formDataToSend.append(key, JSON.stringify(data[key]));
        } else if (data[key] instanceof File) {
          formDataToSend.append(key, data[key]);
        } else {
          // Convert booleans to strings explicitly for FormData
          formDataToSend.append(key, String(data[key]));
        }
      });
      
      // Mark as draft (auto-save)
      formDataToSend.append('profileCompleted', 'false');
      formDataToSend.append('isDraft', 'true');
      formDataToSend.append('draftSavedAt', new Date().toISOString());
      
      console.log('💾 Auto-saving to backend (as draft)...', {
        totalFields: Object.keys(data).length,
        timestamp: new Date().toISOString()
      });

      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      const response = await fetch(buildApiUrl('/api/jobseeker/complete-profile'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const result = await response.json();
          console.log('✅ Auto-saved to backend successfully');
          console.log('📊 Backend calculated completion:', result.profileCompletion, '%');
          return true;
        } else {
          console.error('❌ Backend returned non-JSON response');
          return false;
        }
      } else {
        console.error('❌ Auto-save to backend failed, status:', response.status);
        return false;
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('⏱️ Auto-save timeout - backend is taking too long');
      } else {
        console.error('Backend auto-save error:', error);
      }
      return false;
    }
  }, []);

  // Initialize form data with auto-save (2-minute periodic save)
  const {
    formData,
    setFormData,
    isSaving,
    saveStatus,
    lastSaveTime,
    lastBackendSaveTime,
    forceSave,
    hasRecentSave,
    clearSavedData
  } = useAutoSave(
    {
      // Personal Information - Only use user context, NO localStorage
      firstName: existingData?.firstName || userData.firstName || user?.firstName || '',
      middleName: existingData?.middleName || '',
      lastName: existingData?.lastName || userData.lastName || user?.lastName || '',
      email: existingData?.email || userData.email || user?.email || '',
      phone: existingData?.phone || userData.phone || user?.phone || user?.phoneNumber || '',
      altPhone: existingData?.altPhone || '',
      dateOfBirth: existingData?.dateOfBirth || '',
      gender: existingData?.gender || '',
      genderOther: existingData?.genderOther || '',
      bloodGroup: existingData?.bloodGroup || '',
      ngo: existingData?.ngo || '',
      hasDisability: existingData?.hasDisability || 'no',
      disabilityDescription: existingData?.disabilityDescription || '',
      communitiesAssociations: existingData?.communitiesAssociations || (existingData?.community ? [{ name: existingData.community, type: 'community', country: '' }] : []),
      profilePhoto: existingData?.profilePhoto || null,
      
      // Nationality & Residency
      nationality: existingData?.nationality || '',
      residentCountry: existingData?.residentCountry || '',
      currentCity: existingData?.currentCity || '',
      postalCode: existingData?.postalCode || '',
      address: existingData?.address || '',
      latitude: existingData?.latitude || '',
      longitude: existingData?.longitude || '',
      workPermit: existingData?.workPermit || '',
      
      // Preferred Working Locations
      preferredLocation1: existingData?.preferredLocation1 || '',
      preferredLocation2: existingData?.preferredLocation2 || '',
      preferredLocation3: existingData?.preferredLocation3 || '',
      willingToRelocate: existingData?.willingToRelocate || '',
      workLocation: existingData?.workLocation || '',
      
      // Professional Profile
      professionalTitle: existingData?.professionalTitle || '',
      yearsExperience: existingData?.yearsExperience || '',
      careerLevel: existingData?.careerLevel || '',
      industry: existingData?.industry || '',
      industryOther: existingData?.industryOther || '',
      summary: existingData?.summary || '',
      
      // Work Experience (Array)
      experienceEntries: existingData?.experienceEntries || [{
        jobTitle: '',
        company: '',
        companyLocation: '',
        employmentType: 'full-time',
        jobIndustry: '',
        jobIndustryOther: '',
        startDate: '',
        endDate: '',
        currentJob: false,
        jobDescription: ''
      }],
      
      // Education (Array)
      educationEntries: existingData?.educationEntries || [{
        degreeType: '',
        fieldOfStudy: '',
        institution: '',
        institutionLocation: '',
        grade: '',
        eduStartYear: '',
        eduEndYear: '',
        eduActivities: ''
      }],
      
      // Skills
      coreSkills: existingData?.coreSkills || [],
      tools: existingData?.tools || [],
      
      // Languages
      languages: existingData?.languages || [],
      
      // Certifications (Array)
      certificationEntries: existingData?.certificationEntries || [{
        certificationName: '',
        certIssuer: '',
        certIssueDate: '',
        certExpiryDate: '',
        credentialId: ''
      }],
      
      // Professional Memberships
      membershipOrg: existingData?.membershipOrg || '',
      membershipType: existingData?.membershipType || '',
      membershipDate: existingData?.membershipDate || '',
      
      // References (Array)
      referenceEntries: existingData?.referenceEntries || [{
        referenceName: '',
        referenceTitle: '',
        referenceCompany: '',
        referenceRelationship: '',
        referenceEmail: '',
        referencePhone: ''
      }],
      
      // Professional Online Presence
      professionalLinks: Array.isArray(existingData?.professionalLinks) ? existingData.professionalLinks : [],
      
      // Job Preferences
      preferredJobTitles: existingData?.preferredJobTitles || '',
      jobType: existingData?.jobType || '',
      noticePeriod: existingData?.noticePeriod || '',
      currentSalary: existingData?.currentSalary || '',
      expectedSalary: existingData?.expectedSalary || '',
      currencyPreference: existingData?.currencyPreference || 'USD',
      currencyPreferenceOther: existingData?.currencyPreferenceOther || '',
      travelAvailability: existingData?.travelAvailability || '',
      
      // Additional Information
      careerObjectives: existingData?.careerObjectives || '',
      hobbies: existingData?.hobbies || '',
      additionalComments: existingData?.additionalComments || '',
      participateInVolunteering: existingData?.participateInVolunteering || '',
      volunteeringDetails: existingData?.volunteeringDetails || '',
      agreeTerms: existingData?.agreeTerms || false,
      allowContact: existingData?.allowContact || false,
      accurateInfo: existingData?.accurateInfo || false
    },
    null, // DISABLED localStorage - backend is single source of truth
    1000, // Debounce delay (1 second)
    backendSaveCallback, // Backend save callback - saves immediately
    false // DISABLED periodic saves - backend auto-save on change is enough
  );

  const [skillInput, setSkillInput] = useState('');
  const [toolInput, setToolInput] = useState('');
  const [skillsDropdownOpen, setSkillsDropdownOpen] = useState(false);
  const skillsDropdownRef = useRef(null);
  const [toolsDropdownOpen, setToolsDropdownOpen] = useState(false);
  const toolsDropdownRef = useRef(null);
  const [openIndustryDropdownIndex, setOpenIndustryDropdownIndex] = useState(null);
  const [languageInput, setLanguageInput] = useState({ language: '', proficiency: '' });
  const [linkInput, setLinkInput] = useState({ type: '', url: '' });
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [sectionProgress, setSectionProgress] = useState({});
  
  // Close skills/tools/industry dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (skillsDropdownRef.current && !skillsDropdownRef.current.contains(e.target)) {
        setSkillsDropdownOpen(false);
      }
      if (toolsDropdownRef.current && !toolsDropdownRef.current.contains(e.target)) {
        setToolsDropdownOpen(false);
      }
      if (openIndustryDropdownIndex !== null && !e.target.closest('.experience-industry-field')) {
        setOpenIndustryDropdownIndex(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openIndustryDropdownIndex]);

  // Update form data when user profile data is fetched - CRITICAL FIX
  useEffect(() => {
    if (userProfileData && Object.keys(userProfileData).length > 0) {
      console.log('📥 Loading saved profile data into form:', userProfileData);
      console.log('🔍 userProfileData keys:', Object.keys(userProfileData));
      
      // Force update all fields with saved data
      setFormData(prev => {
        // Helper to get value - ALWAYS prioritize saved data
        const getValue = (savedVal, prevVal = '', fallback = '') => {
          // Priority 1: Saved data from backend
          if (savedVal !== undefined && savedVal !== null && savedVal !== '') {
            return savedVal;
          }
          // Priority 2: Previous form state (if user just typed something)
          if (prevVal !== undefined && prevVal !== null && prevVal !== '') {
            return prevVal;
          }
          // Priority 3: Fallback
          return fallback;
        };
        
        // Create complete new state object
        const newState = {
          // Keep prev as base to maintain any user inputs
          ...prev,
          
          // Personal Information - FORCE LOAD from saved data
          firstName: getValue(userProfileData.firstName, prev.firstName, user?.firstName || ''),
          middleName: getValue(userProfileData.middleName, prev.middleName),
          lastName: getValue(userProfileData.lastName, prev.lastName, user?.lastName || ''),
          email: getValue(userProfileData.email, prev.email, user?.email || ''),
          phone: getValue(userProfileData.phone, prev.phone, user?.phone || ''),
          altPhone: getValue(userProfileData.altPhone, prev.altPhone),
          dateOfBirth: getValue(userProfileData.dateOfBirth, prev.dateOfBirth),
          gender: getValue(userProfileData.gender, prev.gender),
          genderOther: getValue(userProfileData.genderOther, prev.genderOther),
          bloodGroup: getValue(userProfileData.bloodGroup, prev.bloodGroup),
          ngo: getValue(userProfileData.ngo, prev.ngo),
          hasDisability: getValue(userProfileData.hasDisability, prev.hasDisability, 'no'),
          disabilityDescription: getValue(userProfileData.disabilityDescription, prev.disabilityDescription),
          communitiesAssociations: getValue(userProfileData.communitiesAssociations, prev.communitiesAssociations) || (getValue(userProfileData.community, prev.community) ? [{ name: getValue(userProfileData.community, prev.community), type: 'community', country: '' }] : []),
          
          // Nationality & Residency
          nationality: getValue(userProfileData.nationality, prev.nationality),
          residentCountry: getValue(userProfileData.residentCountry, prev.residentCountry),
          currentCity: getValue(userProfileData.currentCity || userProfileData.location?.city, prev.currentCity),
          postalCode: getValue(userProfileData.postalCode, prev.postalCode),
          address: getValue(userProfileData.address, prev.address),
          latitude: getValue(userProfileData.latitude || userProfileData.location?.coordinates?.[1], prev.latitude),
          longitude: getValue(userProfileData.longitude || userProfileData.location?.coordinates?.[0], prev.longitude),
          workPermit: getValue(userProfileData.workPermit, prev.workPermit),
          
          // Preferred Locations
          preferredLocation1: getValue(userProfileData.preferredLocation1, prev.preferredLocation1),
          preferredLocation2: getValue(userProfileData.preferredLocation2, prev.preferredLocation2),
          preferredLocation3: getValue(userProfileData.preferredLocation3, prev.preferredLocation3),
          willingToRelocate: getValue(userProfileData.willingToRelocate, prev.willingToRelocate),
          workLocation: getValue(userProfileData.workLocation, prev.workLocation),
          
          // Professional Profile
          professionalTitle: getValue(userProfileData.professionalTitle || userProfileData.experience?.[0]?.jobTitle, prev.professionalTitle),
          yearsExperience: getValue(userProfileData.yearsExperience || userProfileData.yearsOfExperience, prev.yearsExperience),
          careerLevel: getValue(userProfileData.careerLevel, prev.careerLevel),
          industry: getValue(userProfileData.industry, prev.industry),
          industryOther: getValue(userProfileData.industryOther, prev.industryOther),
          summary: getValue(userProfileData.summary || userProfileData.bio || userProfileData.professionalSummary, prev.summary),
          
          // Skills - ALWAYS use saved arrays if they exist
          coreSkills: (userProfileData.technicalSkills || userProfileData.coreSkills || prev.coreSkills || []),
          tools: (userProfileData.tools || prev.tools || []),
          languages: (userProfileData.languages || prev.languages || []),
          
          // Experience and Education - CRITICAL: Always load saved arrays AND ensure at least one entry for better UX
          experienceEntries: (() => {
            const entries = userProfileData.experience || userProfileData.experienceEntries || prev.experienceEntries || [];
            return entries.length > 0 ? entries : [{
              jobTitle: '', company: '', companyLocation: '', employmentType: 'full-time',
              jobIndustry: '', jobIndustryOther: '', startDate: '', endDate: '',
              currentJob: false, jobDescription: ''
            }];
          })(),
          educationEntries: (() => {
            const entries = userProfileData.education || userProfileData.educationEntries || prev.educationEntries || [];
            return entries.length > 0 ? entries : [{
              degreeType: '', fieldOfStudy: '', institution: '', institutionLocation: '',
              grade: '', eduStartYear: '', eduEndYear: '', eduActivities: ''
            }];
          })(),
          certificationEntries: (() => {
            const entries = userProfileData.certifications || userProfileData.certificationEntries || prev.certificationEntries || [];
            return entries.length > 0 ? entries : [{
              certificationName: '', certIssuer: '', certIssueDate: '',
              certExpiryDate: '', credentialId: ''
            }];
          })(),
          
          // Professional Memberships - FIX: Load from backend
          membershipOrg: getValue(userProfileData.membershipOrg, prev.membershipOrg),
          membershipType: getValue(userProfileData.membershipType, prev.membershipType),
          membershipDate: getValue(userProfileData.membershipDate, prev.membershipDate),
          
          // Professional References - FIX: Load from backend AND ensure at least one entry
          referenceEntries: (() => {
            const entries = userProfileData.references || userProfileData.referenceEntries || prev.referenceEntries || [];
            return entries.length > 0 ? entries : [{
              referenceName: '', referenceTitle: '', referenceCompany: '',
              referenceRelationship: '', referenceEmail: '', referencePhone: ''
            }];
          })(),
          
          // Job Preferences
          preferredJobTitles: getValue(userProfileData.preferredJobTitles, prev.preferredJobTitles),
          jobType: getValue(userProfileData.jobType || userProfileData.preferredJobType, prev.jobType),
          noticePeriod: getValue(userProfileData.noticePeriod, prev.noticePeriod),
          currentSalary: getValue(userProfileData.currentSalary, prev.currentSalary),
          expectedSalary: getValue(userProfileData.expectedSalary, prev.expectedSalary),
          preferredLocations: getValue(userProfileData.preferredLocations, prev.preferredLocations),
          currencyPreference: getValue(userProfileData.currencyPreference, prev.currencyPreference),
          travelAvailability: getValue(userProfileData.travelAvailability, prev.travelAvailability),
          
          // Professional Links
          professionalLinks: (userProfileData.professionalLinks || prev.professionalLinks || []),
          
          // Additional fields
          portfolio: getValue(userProfileData.portfolio, prev.portfolio),
          website: getValue(userProfileData.website, prev.website),
          linkedIn: getValue(userProfileData.linkedIn, prev.linkedIn),
          github: getValue(userProfileData.github, prev.github),
          twitter: getValue(userProfileData.twitter, prev.twitter),
          
          // Additional Information
          careerObjectives: getValue(userProfileData.careerObjectives, prev.careerObjectives),
          hobbies: getValue(userProfileData.hobbies, prev.hobbies),
          additionalComments: getValue(userProfileData.additionalComments, prev.additionalComments),
          
          // Checkboxes - CRITICAL: Must check explicitly for boolean values
          agreeTerms: userProfileData.agreeTerms !== undefined ? userProfileData.agreeTerms : (prev.agreeTerms || false),
          allowContact: userProfileData.allowContact !== undefined ? userProfileData.allowContact : (prev.allowContact || false),
          accurateInfo: userProfileData.accurateInfo !== undefined ? userProfileData.accurateInfo : (prev.accurateInfo || false),
        };
        
        console.log('✅ New form state created with saved data');
        console.log('📊 Sample fields:', {
          firstName: newState.firstName,
          nationality: newState.nationality,
          professionalTitle: newState.professionalTitle,
          experienceCount: newState.experienceEntries?.length,
          educationCount: newState.educationEntries?.length,
          referencesCount: newState.referenceEntries?.length,
          membershipOrg: newState.membershipOrg ? '✓' : '✗'
        });
        console.log('📊 Checkbox states loaded:', {
          agreeTerms: newState.agreeTerms,
          allowContact: newState.allowContact,
          accurateInfo: newState.accurateInfo
        });
        
        // Backend is the single source of truth - no localStorage caching
        console.log('✅ All data loaded from backend (no localStorage conflicts)');
        
        return newState;
      });
      
      // Log loaded data for debugging
      console.log('✅ Form data loaded from saved profile');
      console.log('📊 Loaded arrays:', {
        experience: userProfileData.experience?.length || userProfileData.experienceEntries?.length || 0,
        education: userProfileData.education?.length || userProfileData.educationEntries?.length || 0,
        certifications: userProfileData.certifications?.length || userProfileData.certificationEntries?.length || 0,
        references: userProfileData.references?.length || userProfileData.referenceEntries?.length || 0,
        skills: userProfileData.technicalSkills?.length || userProfileData.coreSkills?.length || 0,
        tools: userProfileData.tools?.length || 0,
        languages: userProfileData.languages?.length || 0,
        professionalLinks: userProfileData.professionalLinks?.length || 0
      });
      console.log('📊 Loaded membership data:', {
        membershipOrg: userProfileData.membershipOrg || '(empty)',
        membershipType: userProfileData.membershipType || '(empty)',
        membershipDate: userProfileData.membershipDate || '(empty)'
      });
    }
  }, [userProfileData, user, setFormData]);

  // Map behavior (moved to LocationMap)

  // Calculate progress - 100% accurate calculation with REAL-TIME updates
  useEffect(() => {
    const calculateProgress = () => {
      // Use REAL-TIME local calculation for progress as user fills the form
      // Backend progress is only used as initial reference, not for live updates
      // This ensures progress bar updates immediately as user types
      
      let filledFields = 0;
      let totalFields = 0;
      let sectionProgress = {};

      // Helper function to check if a field has meaningful content
      const hasContent = (value) => {
        if (value === null || value === undefined) return false;
        if (typeof value === 'string') return value.trim().length > 0;
        if (typeof value === 'boolean') return true;
        if (Array.isArray(value)) return value.length > 0;
        return true;
      };

      // Helper function to check if an array entry has meaningful content
      const hasArrayContent = (array, requiredFields = []) => {
        if (!Array.isArray(array) || array.length === 0) return false;
        return array.some(entry => {
          if (requiredFields.length === 0) {
            return Object.values(entry).some(val => hasContent(val));
          }
          return requiredFields.every(field => hasContent(entry[field]));
        });
      };

      // 1. Personal Information Section (8 fields)
      const personalFields = [
        { name: 'firstName', required: true },
        { name: 'lastName', required: true },
        { name: 'email', required: true },
        { name: 'phone', required: true },
        { name: 'dateOfBirth', required: true },
        { name: 'gender', required: true },
        { name: 'bloodGroup', required: false },
        { name: 'communitiesAssociations', required: false }
      ];
      
      let personalFilled = 0;
      personalFields.forEach(field => {
        totalFields++;
        if (hasContent(formData[field.name])) {
          filledFields++;
          personalFilled++;
        }
      });
      sectionProgress.personal = Math.round((personalFilled / personalFields.length) * 100);

      // 2. Nationality & Residency Section (7 fields)
      const residencyFields = [
        { name: 'nationality', required: true },
        { name: 'residentCountry', required: true },
        { name: 'currentCity', required: true },
        { name: 'postalCode', required: false },
        { name: 'address', required: false },
        { name: 'latitude', required: false },
        { name: 'workPermit', required: true }
      ];
      
      let residencyFilled = 0;
      residencyFields.forEach(field => {
        totalFields++;
        if (hasContent(formData[field.name])) {
          filledFields++;
          residencyFilled++;
        }
      });
      sectionProgress.residency = Math.round((residencyFilled / residencyFields.length) * 100);

      // 3. Preferred Working Locations Section (5 fields)
      const locationFields = [
        { name: 'preferredLocation1', required: true },
        { name: 'preferredLocation2', required: false },
        { name: 'preferredLocation3', required: false },
        { name: 'willingToRelocate', required: true },
        { name: 'workLocation', required: true }
      ];
      
      let locationFilled = 0;
      locationFields.forEach(field => {
        totalFields++;
        if (hasContent(formData[field.name])) {
          filledFields++;
          locationFilled++;
        }
      });
      sectionProgress.location = Math.round((locationFilled / locationFields.length) * 100);

      // 4. Professional Profile Section (5 fields)
      const professionalFields = [
        { name: 'professionalTitle', required: true },
        { name: 'yearsExperience', required: true },
        { name: 'careerLevel', required: true },
        { name: 'industry', required: true },
        { name: 'summary', required: true }
      ];
      
      let professionalFilled = 0;
      professionalFields.forEach(field => {
        totalFields++;
        if (hasContent(formData[field.name])) {
          filledFields++;
          professionalFilled++;
        }
      });
      sectionProgress.professional = Math.round((professionalFilled / professionalFields.length) * 100);

      // 5. Work Experience Section (Array-based)
      const hasWorkExperience = hasArrayContent(formData.experienceEntries, ['jobTitle', 'company']);
      totalFields += 2; // Count as 2 fields for experience
      if (hasWorkExperience) {
        filledFields += 2;
      }
      sectionProgress.experience = hasWorkExperience ? 100 : 0;

      // 6. Education Section (Array-based)
      const hasEducation = hasArrayContent(formData.educationEntries, ['degreeType', 'institution']);
      totalFields += 2; // Count as 2 fields for education
      if (hasEducation) {
        filledFields += 2;
      }
      sectionProgress.education = hasEducation ? 100 : 0;

      // 7. Skills Section (2 arrays)
      const hasCoreSkills = hasContent(formData.coreSkills);
      const hasTools = hasContent(formData.tools);
      totalFields += 2;
      if (hasCoreSkills) filledFields++;
      if (hasTools) filledFields++;
      sectionProgress.skills = Math.round(((hasCoreSkills ? 1 : 0) + (hasTools ? 1 : 0)) / 2 * 100);

      // 8. Languages Section (Array-based)
      const hasLanguages = hasContent(formData.languages);
      totalFields++;
      if (hasLanguages) filledFields++;
      sectionProgress.languages = hasLanguages ? 100 : 0;

      // 9. Certifications Section (Array-based)
      const hasCertifications = hasArrayContent(formData.certificationEntries, ['certificationName', 'certIssuer']);
      totalFields++;
      if (hasCertifications) filledFields++;
      sectionProgress.certifications = hasCertifications ? 100 : 0;

      // 10. Professional Memberships Section (3 fields)
      const membershipFields = [
        { name: 'membershipOrg', required: false },
        { name: 'membershipType', required: false },
        { name: 'membershipDate', required: false }
      ];
      
      let membershipFilled = 0;
      membershipFields.forEach(field => {
        totalFields++;
        if (hasContent(formData[field.name])) {
          filledFields++;
          membershipFilled++;
        }
      });
      sectionProgress.memberships = Math.round((membershipFilled / membershipFields.length) * 100);

      // 11. References Section (Array-based)
      const hasReferences = hasArrayContent(formData.referenceEntries, ['referenceName', 'referenceTitle']);
      totalFields++;
      if (hasReferences) filledFields++;
      sectionProgress.references = hasReferences ? 100 : 0;

      // 12. Professional Links Section (Array-based)
      const hasProfessionalLinks = hasContent(formData.professionalLinks);
      totalFields++;
      if (hasProfessionalLinks) filledFields++;
      sectionProgress.links = hasProfessionalLinks ? 100 : 0;

      // 13. Job Preferences Section (7 fields)
      const jobPrefFields = [
        { name: 'preferredJobTitles', required: false },
        { name: 'jobType', required: true },
        { name: 'noticePeriod', required: true },
        { name: 'currentSalary', required: false },
        { name: 'expectedSalary', required: false },
        { name: 'currencyPreference', required: false },
        { name: 'travelAvailability', required: false }
      ];
      
      let jobPrefFilled = 0;
      jobPrefFields.forEach(field => {
        totalFields++;
        if (hasContent(formData[field.name])) {
          filledFields++;
          jobPrefFilled++;
        }
      });
      sectionProgress.jobPreferences = Math.round((jobPrefFilled / jobPrefFields.length) * 100);

      // 14. Additional Information Section (4 fields)
      const additionalFields = [
        { name: 'careerObjectives', required: false },
        { name: 'hobbies', required: false },
        { name: 'additionalComments', required: false },
        { name: 'agreeTerms', required: true }
      ];
      
      let additionalFilled = 0;
      additionalFields.forEach(field => {
        totalFields++;
        if (hasContent(formData[field.name])) {
          filledFields++;
          additionalFilled++;
        }
      });
      sectionProgress.additional = Math.round((additionalFilled / additionalFields.length) * 100);

      // Calculate overall progress
      const progress = Math.min((filledFields / totalFields) * 100, 100);
      const roundedProgress = Math.round(progress);
      
      // Use BACKEND's value when available for consistency with Dashboard and MyProfile
      // Fall back to local calculation for real-time updates while filling form
      const backendCompletion = userProfileData?.profileCompletion || userProfileData?.profileStatus?.completionPercentage;
      const finalProgress = backendCompletion || roundedProgress;
      
      setProgressPercentage(finalProgress);
      setSectionProgress(sectionProgress);
      
      // Debug logging
      console.log('📊 Progress Calculation:', {
        filledFields,
        totalFields,
        localProgress: roundedProgress,
        backendProgress: backendCompletion,
        usingProgress: finalProgress,
        consistency: backendCompletion ? '✅ Using BACKEND for consistency' : '⚠️ Using LOCAL (backend not available)',
        sectionProgress
      });
    };

    calculateProgress();
  }, [userProfileData, formData]); // Recalculate when backend data OR form data changes for real-time progress

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Hide validation message when user makes a selection
    const validationMessage = e.target.closest('.form-group-comprehensive')?.querySelector('.validation-message');
    if (validationMessage) {
      validationMessage.style.display = 'none';
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      // Check file size (50KB = 50 * 1024 bytes)
      if (file.size > 50 * 1024) {
        alert('Profile photo must be 50KB or smaller. Please compress your image.');
        e.target.value = ''; // Clear the file input
        return;
      }
      setFormData(prev => ({ ...prev, profilePhoto: file }));
      const reader = new FileReader();
      reader.onload = (e) => setProfilePhotoPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      alert('Please select a valid image file (JPG, PNG, GIF, etc.)');
      e.target.value = ''; // Clear the file input
    }
  };

  // Experience handlers
  const addExperience = () => {
    setFormData(prev => ({
      ...prev,
      experienceEntries: [...prev.experienceEntries, {
        jobTitle: '',
        company: '',
        companyLocation: '',
        employmentType: 'full-time',
        jobIndustry: '',
        startDate: '',
        endDate: '',
        currentJob: false,
        jobDescription: ''
      }]
    }));
  };

  const removeExperience = (index) => {
    setFormData(prev => ({
      ...prev,
      experienceEntries: prev.experienceEntries.filter((_, i) => i !== index)
    }));
  };

  const handleExperienceChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      experienceEntries: prev.experienceEntries.map((exp, i) => 
        i === index ? { ...exp, [field]: value } : exp
      )
    }));
  };

  // Education handlers
  const addEducation = () => {
    setFormData(prev => ({
      ...prev,
      educationEntries: [...prev.educationEntries, {
        degreeType: '',
        fieldOfStudy: '',
        institution: '',
        institutionLocation: '',
        grade: '',
        eduStartYear: '',
        eduEndYear: '',
        eduActivities: ''
      }]
    }));
  };

  const removeEducation = (index) => {
    setFormData(prev => ({
      ...prev,
      educationEntries: prev.educationEntries.filter((_, i) => i !== index)
    }));
  };

  const handleEducationChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      educationEntries: prev.educationEntries.map((edu, i) => 
        i === index ? { ...edu, [field]: value } : edu
      )
    }));
  };

  // Skills handlers
  const addSkill = () => {
    const skill = skillInput.trim();
    if (skill && !formData.coreSkills.includes(skill)) {
      setFormData(prev => ({
        ...prev,
        coreSkills: [...prev.coreSkills, skill]
      }));
      setSkillInput('');
    }
  };

  const removeSkill = (index) => {
    setFormData(prev => ({
      ...prev,
      coreSkills: prev.coreSkills.filter((_, i) => i !== index)
    }));
  };

  const addTool = () => {
    const tool = toolInput.trim();
    if (tool && !formData.tools.includes(tool)) {
      setFormData(prev => ({
        ...prev,
        tools: [...prev.tools, tool]
      }));
      setToolInput('');
    }
  };

  const removeTool = (index) => {
    setFormData(prev => ({
      ...prev,
      tools: prev.tools.filter((_, i) => i !== index)
    }));
  };

  // Language handlers
  const addLanguage = () => {
    if (languageInput.language && languageInput.proficiency) {
      const langExists = formData.languages.some(l => l.language === languageInput.language);
      if (!langExists) {
        setFormData(prev => ({
          ...prev,
          languages: [...prev.languages, { ...languageInput }]
        }));
        setLanguageInput({ language: '', proficiency: '' });
      }
    } else {
      alert('Please enter both language and proficiency level');
    }
  };

  const removeLanguage = (index) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.filter((_, i) => i !== index)
    }));
  };

  // Certification handlers
  const addCertification = () => {
    setFormData(prev => ({
      ...prev,
      certificationEntries: [...prev.certificationEntries, {
        certificationName: '',
        certIssuer: '',
        certIssueDate: '',
        certExpiryDate: '',
        credentialId: ''
      }]
    }));
  };

  const removeCertification = (index) => {
    setFormData(prev => ({
      ...prev,
      certificationEntries: prev.certificationEntries.filter((_, i) => i !== index)
    }));
  };

  const handleCertificationChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      certificationEntries: prev.certificationEntries.map((cert, i) => 
        i === index ? { ...cert, [field]: value } : cert
      )
    }));
  };

  // Reference handlers
  const addReference = () => {
    setFormData(prev => ({
      ...prev,
      referenceEntries: [...prev.referenceEntries, {
        referenceName: '',
        referenceTitle: '',
        referenceCompany: '',
        referenceRelationship: '',
        referenceEmail: '',
        referencePhone: ''
      }]
    }));
  };

  const removeReference = (index) => {
    setFormData(prev => ({
      ...prev,
      referenceEntries: prev.referenceEntries.filter((_, i) => i !== index)
    }));
  };

  const handleReferenceChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      referenceEntries: prev.referenceEntries.map((ref, i) => 
        i === index ? { ...ref, [field]: value } : ref
      )
    }));
  };

  // Professional Links handlers
  const addLink = () => {
    if (!linkInput.type) {
      alert('Please select a link type');
      return;
    }
    if (!linkInput.url.trim()) {
      alert('Please enter a URL');
      return;
    }
    
    // Validate URL format
    try {
      new URL(linkInput.url);
    } catch (e) {
      alert('Please enter a valid URL (including https://)');
      return;
    }

    setFormData(prev => ({
      ...prev,
      professionalLinks: [...prev.professionalLinks, { ...linkInput }]
    }));
    setLinkInput({ type: '', url: '' });
  };

  const removeLink = (index) => {
    setFormData(prev => ({
      ...prev,
      professionalLinks: prev.professionalLinks.filter((_, i) => i !== index)
    }));
  };

  // New function to save to backend (for both manual "Save Now" and auto-save)
  const saveToBackend = async (showSuccessMessage = false, redirectAfterSave = false, asComplete = false) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('⚠️  No token found, skipping backend save');
        return false;
      }

      const formDataToSend = new FormData();
      
      // Append all fields
      Object.keys(formData).forEach(key => {
        if (Array.isArray(formData[key])) {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else if (formData[key] instanceof File) {
          formDataToSend.append(key, formData[key]);
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      // Mark as draft or complete
      if (asComplete) {
        formDataToSend.append('profileCompleted', 'true');
        formDataToSend.append('isDraft', 'false');
      } else {
        formDataToSend.append('profileCompleted', 'false');
        formDataToSend.append('isDraft', 'true');
        formDataToSend.append('draftSavedAt', new Date().toISOString());
      }
      
      console.log('💾 Saving to backend...', {
        totalFields: Object.keys(formData).length,
        timestamp: new Date().toISOString()
      });

      const response = await fetch(buildApiUrl('/api/jobseeker/complete-profile'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      const responseClone = response.clone();
      let data;
      
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('❌ Failed to parse response as JSON:', jsonError);
        const textResponse = await responseClone.text();
        console.error('Response text:', textResponse);
        if (showSuccessMessage) {
          setSubmitError('Server error occurred. Please try again.');
        }
        return false;
      }

      if (response.ok) {
        // Keep saved form data for later resumption
        localStorage.setItem('profileCompleted', asComplete ? 'true' : 'false');
        localStorage.setItem('isDraft', asComplete ? 'false' : 'true');
        
        console.log('✅ Successfully saved to backend');
        
        if (showSuccessMessage) {
          setSubmitError('✓ Data saved successfully!' + (redirectAfterSave ? ' Redirecting to dashboard...' : ''));
        }
        
        if (redirectAfterSave) {
          setTimeout(() => {
            window.location.href = '/jobseeker-dashboard';
          }, asComplete ? 800 : 1500);
        }
        
        return true;
      } else {
        console.error('❌ Backend save failed:', data);
        
        // Check if this is an age restriction error
        if (data.age_restriction) {
          alert('You must be at least 18 years old to create an account. You will be redirected to the homepage.');
          localStorage.removeItem('token');
          localStorage.removeItem('userRole');
          localStorage.removeItem('userId');
          localStorage.removeItem('userEmail');
          localStorage.removeItem('userFirstName');
          localStorage.removeItem('userLastName');
          sessionStorage.clear();
          setTimeout(() => {
            window.location.href = '/';
          }, 1500);
        } else if (showSuccessMessage) {
          setSubmitError(data.error || 'Failed to save. Please try again.');
        }
        
        return false;
      }
    } catch (error) {
      console.error('Backend save error:', error);
      if (showSuccessMessage) {
        setSubmitError('Network error. Please try again.');
      }
      return false;
    }
  };

  // Complete profile: save data and redirect to dashboard
  const handleCompleteProfile = async () => {
    setIsLoading(true);
    setSubmitError('');
    
    try {
      forceSave();
      await saveToBackend(true, true, true);
    } catch (error) {
      console.error('Complete profile error:', error);
      setSubmitError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Go to dashboard without saving
  const handleGoToDashboard = () => {
    navigate('/jobseeker-dashboard');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Show validation messages for required fields
    const form = e.target;
    const requiredFields = form.querySelectorAll('input[required], select[required], textarea[required]');
    
    requiredFields.forEach(field => {
      const formGroup = field.closest('.form-group-comprehensive');
      const validationMessage = formGroup?.querySelector('.validation-message');
      
      if (!field.value && validationMessage) {
        validationMessage.style.display = 'block';
      }
    });
    
    // Check if any required fields are empty
    const hasEmptyRequiredFields = Array.from(requiredFields).some(field => !field.value);
    if (hasEmptyRequiredFields) {
      return; // Stop submission if there are empty required fields
    }
    
    // Age verification - User must be 18 or older
    if (formData.dateOfBirth) {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      // Adjust age if birthday hasn't occurred this year yet
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      if (age < 18) {
        alert('You must be at least 18 years old to create an account. You will be redirected to the homepage.');
        // Clear user session and redirect to homepage
        localStorage.removeItem('token');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userId');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userFirstName');
        localStorage.removeItem('userLastName');
        sessionStorage.clear();
        setTimeout(() => {
          window.location.href = '/';
        }, 1500);
        return;
      }
    }
    
    // Validation
    if (formData.coreSkills.length === 0) {
      alert('Please add at least one skill');
      return;
    }

    if (formData.languages.length === 0) {
      alert('Please add at least one language');
      return;
    }

    if (!formData.agreeTerms) {
      alert('Please agree to the Terms of Service and Privacy Policy');
      return;
    }

    if (!formData.accurateInfo) {
      alert('Please confirm that all information provided is accurate and truthful');
      return;
    }

    setIsLoading(true);
    setSubmitError('');

    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();
      
      // Append all fields
      Object.keys(formData).forEach(key => {
        if (Array.isArray(formData[key])) {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else if (formData[key] instanceof File) {
          formDataToSend.append(key, formData[key]);
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      // Mark profile as completed
      formDataToSend.append('profileCompleted', 'true');
      formDataToSend.append('hasCompletedProfile', 'true');
      
      console.log('📋 Registration form - Profile completion flags added to payload');
      console.log('📋 Registration form - Sending to:', buildApiUrl('/api/jobseeker/complete-profile'));

      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      const response = await fetch(buildApiUrl('/api/jobseeker/complete-profile'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // Check if response is JSON before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server returned non-JSON response (502 Bad Gateway). Backend may be down.');
      }

      const data = await response.json();
      
      console.log('📋 Registration form - Response status:', response.status);
      console.log('📋 Registration form - Response data:', data);

      if (response.ok) {
        // Clear saved form data on successful submission
        clearSavedData();
        
        // Sync updated identity into auth context and localStorage so header/dashboard show correct name
        try {
          if (formData.firstName) localStorage.setItem('userFirstName', formData.firstName);
          if (formData.lastName) localStorage.setItem('userLastName', formData.lastName);
          if (formData.email) localStorage.setItem('userEmail', formData.email);
          // Mark profile as completed in localStorage
          localStorage.setItem('profileCompleted', 'true');
          localStorage.setItem('hasCompletedProfile', 'true');
          if (typeof updateUser === 'function') {
            updateUser({ 
              firstName: formData.firstName, 
              lastName: formData.lastName, 
              email: formData.email,
              profileCompleted: true,
              hasCompletedProfile: true
            });
          }
        } catch { /* no-op */ }

        // Update user context to mark profile as completed
        alert('Profile completed successfully! Redirecting to your dashboard...');
        
        // Force refresh of user data to ensure profile completion is detected
        setTimeout(() => {
          // Clear any cached data and reload to ensure fresh authentication state
          sessionStorage.clear();
          localStorage.setItem('profileCompleted', 'true');
          localStorage.setItem('hasCompletedProfile', 'true');
          // Reload the page to ensure fresh authentication state
          window.location.href = '/jobseeker-dashboard';
        }, 1000);
      } else {
        // Check if this is an age restriction error
        if (data.age_restriction) {
          alert('You must be at least 18 years old to create an account. You will be redirected to the homepage.');
          // Clear user session and redirect to homepage
          localStorage.removeItem('token');
          localStorage.removeItem('userRole');
          localStorage.removeItem('userId');
          localStorage.removeItem('userEmail');
          localStorage.removeItem('userFirstName');
          localStorage.removeItem('userLastName');
          sessionStorage.clear();
          setTimeout(() => {
            window.location.href = '/';
          }, 1500);
        } else {
          setSubmitError(data.error || 'Failed to submit profile. Please try again.');
        }
      }
    } catch (error) {
      console.error('Submit error:', error);
      if (error.name === 'AbortError') {
        setSubmitError('⏱️ Request timed out. The backend is taking too long to respond. Your data may have been saved. Please refresh and check.');
      } else if (error.message.includes('502') || error.message.includes('Bad Gateway')) {
        setSubmitError('🔴 Backend server is not responding (502 Bad Gateway). Please try again in a moment.');
      } else {
        setSubmitError('Network error: ' + error.message + '. Please check your connection and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="jobseeker-details-comprehensive">
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .spinner {
          animation: spin 1s linear infinite;
        }
      `}</style>
      <style>{`
        /* Style for required/mandatory fields - reddish border */
        .form-group-comprehensive input[required],
        .form-group-comprehensive select[required],
        .form-group-comprehensive textarea[required] {
          border-color: #e74c3c !important;
          border-width: 2px;
        }
        
        /* Style for optional fields - normal orange border */
        .form-group-comprehensive input:not([required]),
        .form-group-comprehensive select:not([required]),
        .form-group-comprehensive textarea:not([required]) {
          border-color: #f39c12;
          border-width: 2px;
        }
        
        /* Custom validation styling to show errors above fields */
        .form-group-comprehensive input:invalid,
        .form-group-comprehensive select:invalid,
        .form-group-comprehensive textarea:invalid {
          border-color: #dc3545 !important;
          box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.2);
        }
        
        .form-group-comprehensive input:invalid + .validation-message,
        .form-group-comprehensive select:invalid + .validation-message,
        .form-group-comprehensive textarea:invalid + .validation-message,
        .form-group-comprehensive .validation-message {
          display: none;
        }
        
        .form-group-comprehensive:has(input:invalid) .validation-message,
        .form-group-comprehensive:has(select:invalid) .validation-message,
        .form-group-comprehensive:has(textarea:invalid) .validation-message {
          display: block;
        }
        
        .validation-message {
          display: none;
          position: absolute;
          top: -35px;
          left: 0;
          right: 0;
          background: #fff5f5;
          border: 1px solid #dc3545;
          border-radius: 6px;
          padding: 8px 12px;
          font-size: 12px;
          color: #dc3545;
          z-index: 10;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        
        .validation-message::before {
          content: '';
          position: absolute;
          top: 100%;
          left: 20px;
          border: 5px solid transparent;
          border-top-color: #dc3545;
        }
        
        .validation-message::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 19px;
          border: 4px solid transparent;
          border-top-color: #fff5f5;
        }
        
        .form-group-comprehensive {
          position: relative;
        }
        
        .radio-group-comprehensive {
          position: relative;
        }
        
        .radio-group-comprehensive:invalid + .validation-message {
          display: block;
        }
        
        /* Custom validation for radio groups */
        .radio-group-comprehensive input:invalid {
          border: 2px solid #dc3545;
        }
        
        /* Style for required radio buttons */
        .radio-group-comprehensive input[type="radio"][required] {
          outline: 2px solid #e74c3c;
          outline-offset: 2px;
        }
        
        /* Ensure validation messages appear above all form elements */
        .form-group-comprehensive input:focus:invalid + .validation-message,
        .form-group-comprehensive select:focus:invalid + .validation-message,
        .form-group-comprehensive textarea:focus:invalid + .validation-message {
          display: block;
        }
        
        /* Better visibility for required field labels */
        .required-comprehensive {
          color: #e74c3c;
          font-weight: bold;
          margin-left: 3px;
        }

        /* Mobile: force vertical section layout (title above fields) */
        @media (max-width: 768px) {
          .jobseeker-details-comprehensive .section-comprehensive {
            display: block !important;
            width: 100% !important;
            max-width: 100% !important;
            overflow: hidden !important;
            background: #ffffff !important;
          }
          .jobseeker-details-comprehensive .section-title-comprehensive {
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            text-align: center !important;
            gap: 8px !important;
            width: 100% !important;
            max-width: 100% !important;
            flex: 0 0 auto !important;
            box-sizing: border-box !important;
          }
          .jobseeker-details-comprehensive .section-title-comprehensive > span {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 8px !important;
            width: 100% !important;
          }
          .jobseeker-details-comprehensive .checkbox-group-comprehensive input[type="checkbox"] {
            display: inline-block !important;
            visibility: visible !important;
            opacity: 1 !important;
            width: 22px !important;
            height: 22px !important;
            min-width: 22px !important;
            min-height: 22px !important;
            max-width: 22px !important;
            flex: 0 0 22px !important;
            position: relative !important;
            left: 0 !important;
            -webkit-appearance: checkbox !important;
            appearance: checkbox !important;
            accent-color: #0d9488 !important;
          }
          .jobseeker-details-comprehensive .section-content-comprehensive {
            display: block !important;
            width: 100% !important;
            max-width: 100% !important;
            padding: 0 16px 16px !important;
            box-sizing: border-box !important;
          }
          .jobseeker-details-comprehensive .form-row-comprehensive {
            display: flex !important;
            flex-direction: column !important;
            width: 100% !important;
            max-width: 100% !important;
            grid-template-columns: 1fr !important;
          }
          .jobseeker-details-comprehensive .form-group-comprehensive,
          .jobseeker-details-comprehensive .experience-item-comprehensive {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            box-sizing: border-box !important;
          }
          .jobseeker-details-comprehensive .form-group-comprehensive input,
          .jobseeker-details-comprehensive .form-group-comprehensive select,
          .jobseeker-details-comprehensive .form-group-comprehensive textarea {
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
        }
      `}</style>
      <style>{`
        @media (min-width: 769px) {
          .jobseeker-details-comprehensive {
            max-width: 100% !important;
          }
          .jobseeker-header {
            width: 100% !important;
            max-width: 100% !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .jobseeker-header .header-container {
            display: flex !important;
            align-items: center !important;
            justify-content: flex-start !important;
            padding: 12px 20px 12px 10px !important;
            max-width: 100% !important;
            margin: 0 !important;
            width: 100% !important;
          }
          .jobseeker-header .header-left {
            display: flex !important;
            flex-direction: row !important;
            align-items: center !important;
            gap: 12px !important;
            margin-right: auto !important;
            margin-left: 0 !important;
            padding-left: 0 !important;
            flex: 0 0 auto !important;
            order: 1 !important;
          }
          .jobseeker-header .header-left .logo-icon {
            flex-shrink: 0 !important;
          }
          .jobseeker-header .header-left .logo-text {
            flex-shrink: 0 !important;
          }
          .jobseeker-header .header-right {
            margin-left: auto !important;
            flex: 0 0 auto !important;
            order: 2 !important;
          }
        }
      `}</style>
      {/* Header */}
      <header className="jobseeker-header">
        <div className="header-container">
          <div className="header-left" onClick={() => navigate('/jobseeker-dashboard')}>
            <div className="logo-icon">
              <img src="/AK_logo.png" alt="AksharJobs Logo" className="header-logo-img" />
            </div>
            <div className="logo-text">
              <h3 className="logo-title">AksharJobs</h3>
              <p className="logo-subtitle">Job Seeker Profile</p>
            </div>
          </div>
          <div className="header-right">
            <div className="header-controls">
              <div className="auto-save-status">
                {isSaving && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#f39c12' }}>
                    <div className="spinner" style={{ width: '12px', height: '12px', border: '2px solid #f39c12', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                    <span>Auto-saving...</span>
                  </div>
                )}
                {saveStatus === 'saved' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#27ae60' }}>
                    <FontAwesomeIcon icon={faCheck} />
                    <span>Auto-saved {lastSaveTime && `at ${lastSaveTime.toLocaleTimeString()}`}</span>
                  </div>
                )}
                {saveStatus === 'error' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#e74c3c' }}>
                    <FontAwesomeIcon icon={faTimes} />
                    <span>Save failed - data preserved locally</span>
                  </div>
                )}
                {saveStatus === 'loaded' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#3498db' }}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <span>Previous data restored</span>
                  </div>
                )}
                {!isSaving && !saveStatus && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#6c757d' }}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <span>Auto-save enabled (every 2 minutes)</span>
                  </div>
                )}
              </div>
              <button
                className="complete-profile-btn"
                type="button"
                onClick={handleCompleteProfile}
                disabled={isSaving || isLoading}
              >
                <FontAwesomeIcon icon={faCheckCircle} />
                <span className="btn-text">{(isSaving || isLoading) ? 'Saving...' : 'Complete profile'}</span>
              </button>
              <button
                className="go-to-dashboard-btn"
                type="button"
                onClick={handleGoToDashboard}
              >
                <FontAwesomeIcon icon={faRocket} />
                <span className="btn-text">Go to dashboard</span>
              </button>
              <button
                className="clear-form-btn"
                type="button"
                onClick={() => {
                  if (window.confirm('Are you sure you want to clear all saved form data? This cannot be undone.')) {
                    clearSavedData();
                    window.location.reload();
                  }
                }}
              >
                <span className="btn-text">Clear Form Data</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="jobseeker-main">
        <div className="jobseeker-container">
          <div className="jobseeker-form-card">
            {/* Main Form */}
            <div className="jobseeker-form-container">
        <form onSubmit={handleSubmit}>
          {submitError && (
            <div className="error-banner" style={{
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px',
              background: submitError.includes('✓') ? '#d4edda' : '#f8d7da',
              color: submitError.includes('✓') ? '#155724' : '#721c24',
              border: submitError.includes('✓') ? '1px solid #c3e6cb' : '1px solid #f5c6cb'
            }}>
              {submitError}
            </div>
          )}

          {/* Draft Banner */}
          {userProfileData?.isDraft && (
            <div style={{
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px',
              background: '#fef3c7',
              color: '#92400e',
              border: '1px solid #f59e0b',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <FontAwesomeIcon icon={faEdit} />
              <div>
                <strong>Resuming Draft Profile</strong>
                <p style={{ margin: '5px 0 0 0', fontSize: '14px' }}>
                  Your previously saved draft has been loaded. Complete the remaining fields and submit to finish your profile.
                </p>
              </div>
            </div>
          )}

          {/* Enhanced Progress Section */}
          <div className="progress-section-comprehensive">
            <div className="progress-header-comprehensive">
              <h2 className="progress-title-comprehensive">
                <FontAwesomeIcon icon={faCheckCircle} className="progress-title-icon" />
                Profile Completion
              </h2>
              <div className="progress-percentage-badge">
                {progressPercentage}%
              </div>
            </div>
            
            <div className="progress-bar-comprehensive">
              <div
                className="progress-fill-comprehensive"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>

            {/* Progress Status Message */}
            <div className="progress-status-message">
              {progressPercentage === 100 ? (
                <span style={{ color: '#0d9488', fontWeight: '600' }}>
                  🎉 Profile Complete! Ready to submit.
                </span>
              ) : progressPercentage >= 80 ? (
                <span style={{ color: '#000000' }}>
                  Almost there! Just a few more fields to complete.
                </span>
              ) : progressPercentage >= 50 ? (
                <span style={{ color: '#000000' }}>
                  Good progress! Keep filling out the form.
                </span>
              ) : (
                <span style={{ color: '#000000' }}>
                  Let's get started! Fill out your profile information.
                </span>
              )}
            </div>

            {/* Quick Stats */}
            <div className="progress-stats-grid">
              <div className="progress-stat-item">
                <div className="progress-stat-value">14</div>
                <div className="progress-stat-label">Sections</div>
              </div>
              <div className="progress-stat-item">
                <div className="progress-stat-value">50+</div>
                <div className="progress-stat-label">Fields</div>
              </div>
              <div className="progress-stat-item">
                <div className="progress-stat-value">Auto-save</div>
                <div className="progress-stat-label">Enabled</div>
              </div>
            </div>

            {/* Section Completion Checklist */}
            {progressPercentage < 100 && (
              <div className="sections-checklist-card">
                <div className="sections-checklist-title">
                  Complete these sections:
                </div>
                <div className="sections-checklist-grid">
                  {[
                    { key: 'personal', name: 'Personal Info', icon: '👤' },
                    { key: 'residency', name: 'Nationality', icon: '🌍' },
                    { key: 'location', name: 'Locations', icon: '📍' },
                    { key: 'professional', name: 'Professional', icon: '💼' },
                    { key: 'experience', name: 'Experience', icon: '🏢' },
                    { key: 'education', name: 'Education', icon: '🎓' },
                    { key: 'skills', name: 'Skills', icon: '⚡' },
                    { key: 'languages', name: 'Languages', icon: '🗣️' },
                    { key: 'certifications', name: 'Certifications', icon: '📜' },
                    { key: 'jobPreferences', name: 'Job Prefs', icon: '⚙️' },
                    { key: 'additional', name: 'Additional', icon: '📝' }
                  ].map(section => (
                    <div key={section.key} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      opacity: sectionProgress[section.key] === 100 ? 0.6 : 1
                    }}>
                      <span style={{
                        color: sectionProgress[section.key] === 100 ? '#0d9488' : '#000000',
                        fontSize: '14px'
                      }}>
                        {sectionProgress[section.key] === 100 ? '✅' : section.icon}
                      </span>
                      <span style={{
                        textDecoration: sectionProgress[section.key] === 100 ? 'line-through' : 'none',
                        opacity: sectionProgress[section.key] === 100 ? 0.7 : 1,
                        color: '#000000'
                      }}>
                        {section.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

        {/* Important Instructions Box */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '12px',
          padding: '20px 24px',
          marginBottom: '30px',
          boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
          border: '2px solid rgba(255, 255, 255, 0.2)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '16px'
          }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '50%',
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <FontAwesomeIcon 
                icon={faInfoCircle} 
                style={{ 
                  fontSize: '24px', 
                  color: '#ffffff' 
                }} 
              />
            </div>
            <div style={{ flex: 1, color: '#ffffff' }}>
              <h3 style={{
                margin: '0 0 12px 0',
                fontSize: '18px',
                fontWeight: '700',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>📄 Important Information</span>
              </h3>
              <div style={{
                fontSize: '15px',
                lineHeight: '1.6',
                color: '#ffffff'
              }}>
                <p style={{ margin: '0 0 10px 0', fontWeight: '500' }}>
                  <strong>🚫 No CV Upload Allowed:</strong> We do not allow CV/resume uploads. Your profile in this registration form is your CV. Hence, it is essential to complete this registration form thoroughly.
                </p>
                <p style={{ margin: '0 0 10px 0', fontWeight: '500' }}>
                  <strong>📄 Your Profile is Your CV:</strong> The information you provide in this registration form will be used to generate your professional CV/resume. Make sure to fill all details correctly and provide accurate information.
                </p>
                <p style={{ margin: '0', fontWeight: '500' }}>
                  <strong>💡 Complete 100% for Better Opportunities:</strong> Fill out all sections completely to receive better job recommendations and increase your chances of being matched with the perfect job opportunities.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Personal Information Section */}
        <div className="section-comprehensive">
          <h2 className="section-title-comprehensive" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <FontAwesomeIcon icon={faUser} />
              Personal Information
            </span>
            <span style={{
              background: '#000000',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              {sectionProgress.personal || 0}%
            </span>
          </h2>

          {profilePhotoPreview && (
            <div className="profile-photo-preview-comprehensive" style={{ display: 'block' }}>
              <img src={profilePhotoPreview} alt="Profile Photo" />
            </div>
          )}

          <div className="form-group-comprehensive">
            <label className="file-upload-comprehensive" htmlFor="profilePhoto">
              <FontAwesomeIcon icon={faCamera} />
              <div className="file-upload-text">
                <strong>Upload Photo</strong>
                <p>Click to browse or drag and drop</p>
                <small>Max: 50KB</small>
              </div>
              <input 
                type="file" 
                id="profilePhoto" 
                accept="image/*"
                onChange={handlePhotoUpload}
              />
            </label>
          </div>

          <div className="form-row-comprehensive">
            <div className="form-group-comprehensive">
              <label>First Name <span className="required-comprehensive">*</span></label>
              <input 
                type="text" 
                name="firstName" 
                required 
                placeholder="Enter your first name"
                value={formData.firstName}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group-comprehensive">
              <label>Middle Name</label>
              <input 
                type="text" 
                name="middleName" 
                placeholder="Enter your middle name"
                value={formData.middleName}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group-comprehensive">
              <label>Last Name <span className="required-comprehensive">*</span></label>
              <input 
                type="text" 
                name="lastName" 
                required 
                placeholder="Enter your last name"
                value={formData.lastName}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-row-comprehensive">
            <div className="form-group-comprehensive">
              <label>Date of Birth <span className="required-comprehensive">*</span></label>
              <input 
                type="date" 
                name="dateOfBirth" 
                required
                value={formData.dateOfBirth}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group-comprehensive">
              <label>Gender <span className="required-comprehensive">*</span></label>
              <select 
                name="gender" 
                required
                value={formData.gender}
                onChange={handleInputChange}
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
                <option value="prefer-not-to-say">Prefer not to say</option>
              </select>
              {formData.gender === 'other' && (
                <input
                  type="text"
                  name="genderOther"
                  placeholder="Please specify"
                  value={formData.genderOther}
                  onChange={handleInputChange}
                  style={{ marginTop: '10px' }}
                />
              )}
            </div>
            <div className="form-group-comprehensive">
              <label>Blood Group</label>
              <select 
                name="bloodGroup"
                value={formData.bloodGroup}
                onChange={handleInputChange}
              >
                <option value="">Select blood group</option>
                {BLOOD_GROUPS.map(bg => (
                  <option key={bg} value={bg}>{bg}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row-comprehensive">
            <div className="form-group-comprehensive">
              <label>Email Address <span className="required-comprehensive">*</span></label>
              <input 
                type="email" 
                name="email" 
                required 
                placeholder="your.email@example.com"
                value={formData.email}
                onChange={handleInputChange}
                disabled
              />
            </div>
            <div className="form-group-comprehensive">
              <label>Phone Number <span className="required-comprehensive">*</span></label>
              <input 
                type="tel" 
                name="phone" 
                required 
                placeholder="+254 700 000 000"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group-comprehensive">
              <label>Alternative Phone</label>
              <input 
                type="tel" 
                name="altPhone" 
                placeholder="+1 234 567 8900"
                value={formData.altPhone}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-row-comprehensive">
            <div className="form-group-comprehensive" style={{ width: '100%' }}>
              <label>Community / Association</label>
              <CommunityAssociationSelector
                value={formData.communitiesAssociations || []}
                onChange={(items) => setFormData(prev => ({ ...prev, communitiesAssociations: items }))}
              />
            </div>
          </div>

          <div className="form-row-comprehensive">
            <div className="form-group-comprehensive" style={{ width: '100%' }}>
              <label>Are you connected with any NGO?</label>
              <NGOSelector
                value={formData.ngo || ''}
                onChange={(ngo) => setFormData(prev => ({ ...prev, ngo }))}
              />
            </div>
          </div>

          <div className="form-row-comprehensive">
            <div className="form-group-comprehensive" style={{ width: '100%' }}>
              <label>Do you have any disability?</label>
              <div style={{ display: 'flex', gap: '20px', marginTop: '8px', marginBottom: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 'normal' }}>
                  <input
                    type="radio"
                    name="hasDisability"
                    value="no"
                    checked={formData.hasDisability === 'no'}
                    onChange={handleInputChange}
                    style={{ marginRight: '8px' }}
                  />
                  No
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 'normal' }}>
                  <input
                    type="radio"
                    name="hasDisability"
                    value="yes"
                    checked={formData.hasDisability === 'yes'}
                    onChange={handleInputChange}
                    style={{ marginRight: '8px' }}
                  />
                  Yes
                </label>
              </div>
              {formData.hasDisability === 'yes' && (
                <textarea
                  name="disabilityDescription"
                  placeholder="Please describe"
                  value={formData.disabilityDescription}
                  onChange={handleInputChange}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    marginTop: '8px',
                    resize: 'vertical'
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Nationality & Residency Section */}
        <div className="section-comprehensive">
          <h2 className="section-title-comprehensive" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <FontAwesomeIcon icon={faPassport} />
              Nationality & Residency
            </span>
            <span style={{
              background: '#000000',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              {sectionProgress.residency || 0}%
            </span>
          </h2>

          <div className="form-row-comprehensive">
            <div className="form-group-comprehensive">
              <label>Nationality <span className="required-comprehensive">*</span></label>
              <select 
                name="nationality" 
                required
                value={formData.nationality}
                onChange={handleInputChange}
              >
                <option value="">Select your nationality</option>
                {countries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
            <div className="form-group-comprehensive">
              <label>Resident Country <span className="required-comprehensive">*</span></label>
              <select 
                name="residentCountry" 
                required
                value={formData.residentCountry}
                onChange={handleInputChange}
              >
                <option value="">Select your current country of residence</option>
                {countries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row-comprehensive">
            <div className="form-group-comprehensive">
              <label>Current City <span className="required-comprehensive">*</span></label>
              <input 
                type="text" 
                name="currentCity" 
                required 
                placeholder="e.g., Nairobi"
                value={formData.currentCity}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group-comprehensive">
              <label>Postal/ZIP Code</label>
              <input 
                type="text" 
                name="postalCode" 
                placeholder="Enter postal code"
                value={formData.postalCode}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-group-comprehensive">
            <label>Full Address</label>
            <input 
              type="text" 
              name="address" 
              placeholder="Street address, building, apartment number"
              value={formData.address}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group-comprehensive">
            <label>Pin Your Location on Map</label>
            <div className="info-badge-comprehensive">
              <FontAwesomeIcon icon={faMapMarkerAlt} /> Click on the map to mark your exact location or search for a place
            </div>
            <LocationMap
              latitude={formData.latitude}
              longitude={formData.longitude}
              address={formData.address}
              onLocationChange={(lat, lng) => setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }))}
              onAddressChange={(addr) => setFormData(prev => ({ ...prev, address: addr }))}
            />
            <div style={{ marginTop: '10px', fontSize: '13px', color: '#666' }}>
              <strong>Selected Coordinates:</strong> 
              <span id="coordinates">
                {formData.latitude && formData.longitude 
                  ? `Lat: ${formData.latitude}, Lng: ${formData.longitude}`
                  : 'Click on the map to select'}
              </span>
            </div>
            <input type="hidden" name="latitude" value={formData.latitude} />
            <input type="hidden" name="longitude" value={formData.longitude} />
          </div>

        </div>

        {/* Preferred Working Locations Section */}
        <div className="section-comprehensive">
          <h2 className="section-title-comprehensive">
            <FontAwesomeIcon icon={faMapMarked} />
            Preferred Working Locations
          </h2>

          <div className="form-group-comprehensive">
            <label>Select up to 3 countries where you'd prefer to work <span className="required-comprehensive">*</span></label>
            <div className="info-badge-comprehensive">
              <FontAwesomeIcon icon={faInfoCircle} /> This helps match you with opportunities in your preferred locations
            </div>
          </div>

          <div className="form-row-3-comprehensive">
            <div className="form-group-comprehensive">
              <label>Preferred Location 1 <span className="required-comprehensive">*</span></label>
              <select 
                name="preferredLocation1" 
                required
                value={formData.preferredLocation1}
                onChange={handleInputChange}
              >
                <option value="">Select country</option>
                {countries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
            <div className="form-group-comprehensive">
              <label>Preferred Location 2</label>
              <select 
                name="preferredLocation2"
                value={formData.preferredLocation2}
                onChange={handleInputChange}
              >
                <option value="">Select country</option>
                {countries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
            <div className="form-group-comprehensive">
              <label>Preferred Location 3</label>
              <select 
                name="preferredLocation3"
                value={formData.preferredLocation3}
                onChange={handleInputChange}
              >
                <option value="">Select country</option>
                {countries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row-2-comprehensive">
            <div className="form-group-comprehensive">
              <label>Do you have a valid work permit for your resident country? <span className="required-comprehensive">*</span></label>
              <select 
                name="workPermit" 
                required
                value={formData.workPermit}
                onChange={handleInputChange}
              >
                <option value="">Select an option</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
                <option value="citizen">Citizen</option>
                <option value="not_required">Not Required</option>
              </select>
            </div>

            <div className="form-group-comprehensive">
              <label>Willing to Relocate Internationally? <span className="required-comprehensive">*</span></label>
              <select 
                name="willingToRelocate" 
                required
                value={formData.willingToRelocate}
                onChange={handleInputChange}
              >
                <option value="">Select an option</option>
                <option value="yes">Yes, immediately</option>
                <option value="conditional">Yes, with conditions</option>
                <option value="no">No</option>
              </select>
            </div>
          </div>

          <div className="form-group-comprehensive">
            <label>Work Location Preference <span className="required-comprehensive">*</span></label>
            <select 
              name="workLocation" 
              required
              value={formData.workLocation}
              onChange={handleInputChange}
            >
              <option value="">Select preference</option>
              <option value="onsite">On-site</option>
              <option value="remote">Remote</option>
              <option value="hybrid">Hybrid</option>
              <option value="flexible">Flexible/Open to all</option>
            </select>
          </div>
        </div>

        {/* Professional Profile Section */}
        <div className="section-comprehensive">
          <h2 className="section-title-comprehensive" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <FontAwesomeIcon icon={faBriefcase} />
              Professional Profile (Current Profession)
            </span>
            <span style={{
              background: '#000000',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              {sectionProgress.professional || 0}%
            </span>
          </h2>

          <div className="form-group-comprehensive">
            <label>Professional Title/Headline <span className="required-comprehensive">*</span></label>
            <input 
              type="text" 
              name="professionalTitle" 
              required 
              placeholder="e.g., Senior Software Engineer | Full Stack Developer"
              value={formData.professionalTitle}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-row-comprehensive">
            <div className="form-group-comprehensive">
              <label>Total Years of Experience <span className="required-comprehensive">*</span></label>
              <select 
                name="yearsExperience" 
                required
                value={formData.yearsExperience}
                onChange={handleInputChange}
              >
                <option value="">Select experience</option>
                <option value="0-1">0-1 years (Entry Level)</option>
                <option value="1-3">1-3 years</option>
                <option value="3-5">3-5 years</option>
                <option value="5-7">5-7 years</option>
                <option value="7-10">7-10 years</option>
                <option value="10-15">10-15 years</option>
                <option value="15+">15+ years</option>
              </select>
            </div>
            <div className="form-group-comprehensive">
              <label>Career Level <span className="required-comprehensive">*</span></label>
              <select 
                name="careerLevel" 
                required
                value={formData.careerLevel}
                onChange={handleInputChange}
              >
                <option value="">Select level</option>
                <option value="entry">Entry Level</option>
                <option value="mid">Mid-Level</option>
                <option value="senior">Senior Level</option>
                <option value="lead">Lead/Principal</option>
                <option value="manager">Manager</option>
                <option value="director">Director</option>
                <option value="executive">Executive/C-Level</option>
              </select>
            </div>
          </div>

          <div className="form-group-comprehensive">
            <label>Industry/Sector <span className="required-comprehensive">*</span></label>
            <input
              type="text"
              name="industry" 
              list="primary-industry-options"
              required
              value={formData.industry || ''}
              onChange={handleInputChange}
              placeholder="Select from dropdown or type your industry"
              autoComplete="off"
            />
            <datalist id="primary-industry-options">
              <option value="Technology & IT" />
              <option value="Finance & Banking" />
              <option value="Healthcare & Medical" />
              <option value="Education & Training" />
              <option value="Manufacturing" />
              <option value="Retail & E-commerce" />
              <option value="Hospitality & Tourism" />
              <option value="Construction & Real Estate" />
              <option value="Agriculture & Agribusiness" />
              <option value="Energy & Utilities" />
              <option value="Telecommunications" />
              <option value="Media & Entertainment" />
              <option value="Consulting & Professional Services" />
              <option value="FMCGs and CPGs" />
              <option value="Government & Public Sector" />
              <option value="NGO & Non-Profit" />
              <option value="Legal Services" />
              <option value="Marketing & Advertising" />
              <option value="Logistics & Transportation" />
              <option value="Pharmaceutical & Biotech" />
              <option value="Automotive" />
              <option value="Aerospace & Defense" />
              <option value="Insurance" />
              <option value="Real Estate" />
              <option value="Food & Beverage" />
              <option value="Fashion & Apparel" />
              <option value="Sports & Fitness" />
            </datalist>
            <small style={{ display: 'block', marginTop: '5px', color: '#666', fontSize: '0.85em' }}>
              💡 Tip: You can select from the dropdown or type your own industry
            </small>
          </div>

          <div className="form-group-comprehensive">
            <label>Professional Summary <span className="required-comprehensive">*</span></label>
            <textarea 
              name="summary" 
              required 
              placeholder="Write a compelling summary about your professional background, key achievements, and career goals. Highlight what makes you unique and valuable to potential employers globally. (Minimum 150 characters)"
              value={formData.summary}
              onChange={handleInputChange}
            />
          </div>
        </div>

        {/* Work Experience Section */}
        <div className="section-comprehensive">
          <h2 className="section-title-comprehensive">
            <FontAwesomeIcon icon={faBuilding} />
            Work Experience
          </h2>
          <div className="section-content-comprehensive">
          <div className="info-badge-comprehensive">
            <FontAwesomeIcon icon={faInfoCircle} /> List all relevant work experience starting with the most recent
          </div>
          
          <div id="experienceContainer">
            {formData.experienceEntries.map((experience, index) => (
              <div key={index} className="experience-item-comprehensive">
                {index > 0 && (
                  <button
                    type="button"
                    className="remove-item-btn-comprehensive"
                    onClick={() => removeExperience(index)}
                    title="Remove this experience"
                  >
                    ×
                  </button>
                )}
                
                <div className="form-group-comprehensive" style={{ width: '100%' }}>
                  <label>Job Title <span className="required-comprehensive">*</span></label>
                  <input 
                    type="text" 
                    required 
                    placeholder="e.g., Senior Marketing Manager"
                    value={experience.jobTitle}
                    onChange={(e) => handleExperienceChange(index, 'jobTitle', e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
                
                <div className="form-row-comprehensive" style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
                  <div className="form-group-comprehensive" style={{ width: '100%' }}>
                    <label>Company Name <span className="required-comprehensive">*</span></label>
                    <input 
                      type="text" 
                      required 
                      placeholder="e.g., ABC Corporation"
                      value={experience.company}
                      onChange={(e) => handleExperienceChange(index, 'company', e.target.value)}
                      style={{ width: '100%', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div className="form-group-comprehensive" style={{ width: '100%' }}>
                    <label>Company Location</label>
                    <input 
                      type="text" 
                      placeholder="City, Country"
                      value={experience.companyLocation}
                      onChange={(e) => handleExperienceChange(index, 'companyLocation', e.target.value)}
                      style={{ width: '100%', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
                
                <div className="form-row-comprehensive" style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
                  <div className="form-group-comprehensive" style={{ width: '100%' }}>
                    <label>Employment Type</label>
                    <select
                      value={experience.employmentType}
                      onChange={(e) => handleExperienceChange(index, 'employmentType', e.target.value)}
                      style={{ width: '100%', boxSizing: 'border-box' }}
                    >
                      <option value="full-time">Full-time</option>
                      <option value="part-time">Part-time</option>
                      <option value="contract">Contract</option>
                      <option value="freelance">Freelance</option>
                      <option value="internship">Internship</option>
                    </select>
                  </div>
                  <div className="form-group-comprehensive experience-industry-field" style={{ position: 'relative', width: '100%' }}>
                    <label>Industry</label>
                    <div style={{ position: 'relative', width: '100%', boxSizing: 'border-box' }}>
                      <input 
                        type="text"
                        value={experience.jobIndustry || ''}
                        onChange={(e) => {
                          handleExperienceChange(index, 'jobIndustry', e.target.value);
                          setOpenIndustryDropdownIndex(index);
                        }}
                        onFocus={() => setOpenIndustryDropdownIndex(index)}
                        placeholder="Select or type your industry"
                        autoComplete="off"
                      />
                      {openIndustryDropdownIndex === index && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '100%',
                            left: 0,
                            right: 0,
                            marginBottom: '4px',
                            background: 'white',
                            border: '2px solid #f97316',
                            borderRadius: '8px',
                            boxShadow: '0 4px 15px rgba(249, 115, 22, 0.3)',
                            zIndex: 1000,
                            maxHeight: '200px',
                            overflowY: 'auto'
                          }}
                        >
                          {Array.isArray(industries) && industries
                            .filter(ind => ind.toLowerCase().includes((experience.jobIndustry || '').toLowerCase()))
                            .slice(0, 100)
                            .map((ind, idx) => (
                              <div
                                key={idx}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleExperienceChange(index, 'jobIndustry', ind);
                                  setOpenIndustryDropdownIndex(null);
                                }}
                                style={{
                                  padding: '10px 15px',
                                  cursor: 'pointer',
                                  borderBottom: idx === 0 ? 'none' : '1px solid #f0f0f0',
                                  fontSize: '14px',
                                  color: '#333'
                                }}
                                onMouseEnter={(e) => { e.target.style.background = '#fff7ed'; }}
                                onMouseLeave={(e) => { e.target.style.background = 'white'; }}
                              >
                                {ind}
                              </div>
                            ))}
                          {(experience.jobIndustry || '').trim() && (
                            <div
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleExperienceChange(index, 'jobIndustry', (experience.jobIndustry || '').trim());
                                setOpenIndustryDropdownIndex(null);
                              }}
                              style={{
                                padding: '10px 15px',
                                cursor: 'pointer',
                                borderTop: '2px solid #f97316',
                                fontSize: '14px',
                                color: '#f97316',
                                fontWeight: '600',
                                background: '#fff7ed'
                              }}
                              onMouseEnter={(e) => { e.target.style.background = '#ffedd5'; }}
                              onMouseLeave={(e) => { e.target.style.background = '#fff7ed'; }}
                            >
                              + Add "{experience.jobIndustry}"
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <small style={{ display: 'block', marginTop: '5px', color: '#666', fontSize: '0.85em' }}>
                      💡 Tip: Select from dropdown or type your own
                    </small>
                  </div>
                </div>
                
                <div className="form-row-comprehensive" style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
                  <div className="form-group-comprehensive" style={{ width: '100%' }}>
                    <label>Start Date <span className="required-comprehensive">*</span></label>
                    <input 
                      type="month" 
                      required
                      value={experience.startDate}
                      onChange={(e) => handleExperienceChange(index, 'startDate', e.target.value)}
                      style={{ width: '100%', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div className="form-group-comprehensive" style={{ width: '100%' }}>
                    <label>End Date</label>
                    <input 
                      type="month"
                      value={experience.endDate}
                      onChange={(e) => handleExperienceChange(index, 'endDate', e.target.value)}
                      disabled={experience.currentJob}
                      style={{ width: '100%', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
                
                <div className="checkbox-group-comprehensive">
                  <input 
                    type="checkbox" 
                    id={`currentJob${index}`} 
                    checked={experience.currentJob}
                    onChange={(e) => handleExperienceChange(index, 'currentJob', e.target.checked)}
                  />
                  <label htmlFor={`currentJob${index}`}>I currently work here</label>
                </div>
                
                <div className="form-group-comprehensive" style={{ width: '100%' }}>
                  <label>Key Responsibilities & Achievements</label>
                  <textarea 
                    placeholder="• Led team of 10 marketing professionals&#10;• Increased revenue by 45% through strategic campaigns&#10;• Managed $500K annual budget"
                    value={experience.jobDescription}
                    onChange={(e) => handleExperienceChange(index, 'jobDescription', e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
            ))}
          </div>
          
          <button type="button" className="add-more-btn-comprehensive" onClick={addExperience}>
            <FontAwesomeIcon icon={faPlus} /> Add Another Position
          </button>
          </div>
        </div>

        {/* Education Section */}
        <div className="section-comprehensive">
          <h2 className="section-title-comprehensive">
            <FontAwesomeIcon icon={faGraduationCap} />
            Education
          </h2>
          
          <div id="educationContainer">
            {formData.educationEntries.map((education, index) => (
              <div key={index} className="education-item-comprehensive">
                {index > 0 && (
                  <button
                    type="button"
                    className="remove-item-btn-comprehensive"
                    onClick={() => removeEducation(index)}
                    title="Remove this education"
                  >
                    ×
                  </button>
                )}
                
                <div className="form-group-comprehensive">
                  <label>Degree/Certificate <span className="required-comprehensive">*</span></label>
                  <select
                    required
                    value={education.degreeType}
                    onChange={(e) => handleEducationChange(index, 'degreeType', e.target.value)}
                  >
                    <option value="">Select degree type</option>
                    <option value="high-school">High School Diploma</option>
                    <option value="associate">Associate Degree</option>
                    <option value="bachelor">Bachelor's Degree</option>
                    <option value="master">Master's Degree</option>
                    <option value="phd">Doctorate/PhD</option>
                    <option value="professional">Professional Degree</option>
                    <option value="certificate">Certificate</option>
                    <option value="diploma">Diploma</option>
                  </select>
                </div>
                
                <div className="form-group-comprehensive">
                  <label>Field of Study <span className="required-comprehensive">*</span></label>
                  <input 
                    type="text" 
                    required 
                    placeholder="e.g., Computer Science, Business Administration"
                    value={education.fieldOfStudy}
                    onChange={(e) => handleEducationChange(index, 'fieldOfStudy', e.target.value)}
                  />
                </div>
                
                <div className="form-group-comprehensive">
                  <label>Institution Name <span className="required-comprehensive">*</span></label>
                  <input 
                    type="text" 
                    required 
                    placeholder="e.g., University of Nairobi"
                    value={education.institution}
                    onChange={(e) => handleEducationChange(index, 'institution', e.target.value)}
                  />
                </div>
                
                <div className="form-row-comprehensive">
                  <div className="form-group-comprehensive">
                    <label>Institution Location</label>
                    <input 
                      type="text" 
                      placeholder="City, Country"
                      value={education.institutionLocation}
                      onChange={(e) => handleEducationChange(index, 'institutionLocation', e.target.value)}
                    />
                  </div>
                  <div className="form-group-comprehensive">
                    <label>Grade/GPA</label>
                    <input 
                      type="text" 
                      placeholder="e.g., 3.8/4.0, First Class"
                      value={education.grade}
                      onChange={(e) => handleEducationChange(index, 'grade', e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="form-row-comprehensive">
                  <div className="form-group-comprehensive">
                    <label>Start Year <span className="required-comprehensive">*</span></label>
                    <input 
                      type="number" 
                      required 
                      min="1950" 
                      max="2030" 
                      placeholder="2018"
                      value={education.eduStartYear}
                      onChange={(e) => handleEducationChange(index, 'eduStartYear', e.target.value)}
                    />
                  </div>
                  <div className="form-group-comprehensive">
                    <label>End Year (or Expected)</label>
                    <input 
                      type="number" 
                      min="1950" 
                      max="2035" 
                      placeholder="2022"
                      value={education.eduEndYear}
                      onChange={(e) => handleEducationChange(index, 'eduEndYear', e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="form-group-comprehensive">
                  <label>Activities & Achievements</label>
                  <textarea 
                    placeholder="Academic honors, relevant coursework, extracurricular activities"
                    value={education.eduActivities}
                    onChange={(e) => handleEducationChange(index, 'eduActivities', e.target.value)}
                  />
                </div>
              </div>
            ))}
          </div>
          
          <button type="button" className="add-more-btn-comprehensive" onClick={addEducation}>
            <FontAwesomeIcon icon={faPlus} /> Add Another Education
          </button>
        </div>

        {/* Skills Section */}
        <div className="section-comprehensive">
          <h2 className="section-title-comprehensive">
            <FontAwesomeIcon icon={faLightbulb} />
            Skills & Competencies
          </h2>
          
          <div className="form-group-comprehensive">
            <label>Core Skills <span className="required-comprehensive">*</span></label>
            <div className="info-badge-comprehensive">
              <FontAwesomeIcon icon={faInfoCircle} /> Add skills relevant to your profession and job preferences. Start typing to see suggestions.
            </div>
            <div className="skills-input-container-comprehensive" ref={skillsDropdownRef} style={{ position: 'relative' }}>
              <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
                <input 
                  type="text" 
                  value={skillInput}
                  onChange={(e) => {
                    setSkillInput(e.target.value);
                    setSkillsDropdownOpen(true);
                  }}
                  onFocus={() => setSkillsDropdownOpen(true)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                  placeholder="Enter a skill (e.g., Sales, Project Management)"
                />
                {skillsDropdownOpen && (
                  <div
                    className="skills-suggestions-dropdown"
                    style={{
                      position: 'absolute',
                      bottom: '100%',
                      left: 0,
                      right: 0,
                      marginBottom: '4px',
                      background: 'white',
                      border: '2px solid #f97316',
                      borderRadius: '8px',
                      boxShadow: '0 4px 15px rgba(249, 115, 22, 0.3)',
                      zIndex: 1000,
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}
                  >
                    {Array.isArray(globalSkills?.skills) && globalSkills.skills
                      .filter(s => s.toLowerCase().includes(skillInput.toLowerCase()))
                      .slice(0, 100)
                      .map((s, idx) => (
                        <div
                          key={idx}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!formData.coreSkills.includes(s)) {
                              setFormData(prev => ({ ...prev, coreSkills: [...prev.coreSkills, s] }));
                            }
                            setSkillInput('');
                            setSkillsDropdownOpen(false);
                          }}
                          style={{
                            padding: '10px 15px',
                            cursor: 'pointer',
                            borderBottom: idx === 0 ? 'none' : '1px solid #f0f0f0',
                            fontSize: '14px',
                            color: '#333'
                          }}
                          onMouseEnter={(e) => { e.target.style.background = '#fff7ed'; }}
                          onMouseLeave={(e) => { e.target.style.background = 'white'; }}
                        >
                          {s}
                        </div>
                      ))}
                    {skillInput.trim() && (
                      <div
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          addSkill();
                          setSkillsDropdownOpen(false);
                        }}
                        style={{
                          padding: '10px 15px',
                          cursor: 'pointer',
                          borderTop: '2px solid #f97316',
                          fontSize: '14px',
                          color: '#f97316',
                          fontWeight: '600',
                          background: '#fff7ed'
                        }}
                        onMouseEnter={(e) => { e.target.style.background = '#ffedd5'; }}
                        onMouseLeave={(e) => { e.target.style.background = '#fff7ed'; }}
                      >
                        + Add "{skillInput}"
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button type="button" className="add-btn-comprehensive" onClick={addSkill}>
                <FontAwesomeIcon icon={faPlus} /> Add
              </button>
            </div>
            <div className="tags-container-comprehensive" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
              {formData.coreSkills.map((skill, index) => (
                <div 
                  key={index} 
                  className="tag-comprehensive"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    backgroundColor: '#e7f3ff',
                    color: '#0a66c2',
                    borderRadius: '16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    border: '1px solid #d0e8ff',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#d0e8ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#e7f3ff';
                  }}
                >
                  {skill}
                  <span 
                    className="remove-comprehensive" 
                    onClick={() => removeSkill(index)}
                    style={{
                      cursor: 'pointer',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: '#0a66c2',
                      lineHeight: '1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#0a66c2';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#0a66c2';
                    }}
                  >×</span>
                </div>
              ))}
            </div>
          </div>

          <div className="form-group-comprehensive">
            <label>Software & Tools</label>
            <div className="skills-input-container-comprehensive" ref={toolsDropdownRef} style={{ position: 'relative' }}>
              <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
                <input 
                  type="text" 
                  value={toolInput}
                  onChange={(e) => {
                    setToolInput(e.target.value);
                    setToolsDropdownOpen(true);
                  }}
                  onFocus={() => setToolsDropdownOpen(true)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTool())}
                  placeholder="Enter software/tool (e.g., Microsoft Excel, AutoCAD)"
                />
                {toolsDropdownOpen && (
                  <div
                    className="tools-suggestions-dropdown"
                    style={{
                      position: 'absolute',
                      bottom: '100%',
                      left: 0,
                      right: 0,
                      marginBottom: '4px',
                      background: 'white',
                      border: '2px solid #f97316',
                      borderRadius: '8px',
                      boxShadow: '0 4px 15px rgba(249, 115, 22, 0.3)',
                      zIndex: 1000,
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}
                  >
                    {Array.isArray(softwareAndTools) && softwareAndTools
                      .filter(t => t.toLowerCase().includes(toolInput.toLowerCase()))
                      .slice(0, 100)
                      .map((t, idx) => (
                        <div
                          key={idx}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!formData.tools.includes(t)) {
                              setFormData(prev => ({ ...prev, tools: [...prev.tools, t] }));
                            }
                            setToolInput('');
                            setToolsDropdownOpen(false);
                          }}
                          style={{
                            padding: '10px 15px',
                            cursor: 'pointer',
                            borderBottom: idx === 0 ? 'none' : '1px solid #f0f0f0',
                            fontSize: '14px',
                            color: '#333'
                          }}
                          onMouseEnter={(e) => { e.target.style.background = '#fff7ed'; }}
                          onMouseLeave={(e) => { e.target.style.background = 'white'; }}
                        >
                          {t}
                        </div>
                      ))}
                    {toolInput.trim() && (
                      <div
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          addTool();
                          setToolsDropdownOpen(false);
                        }}
                        style={{
                          padding: '10px 15px',
                          cursor: 'pointer',
                          borderTop: '2px solid #f97316',
                          fontSize: '14px',
                          color: '#f97316',
                          fontWeight: '600',
                          background: '#fff7ed'
                        }}
                        onMouseEnter={(e) => { e.target.style.background = '#ffedd5'; }}
                        onMouseLeave={(e) => { e.target.style.background = '#fff7ed'; }}
                      >
                        + Add "{toolInput}"
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button type="button" className="add-btn-comprehensive" onClick={addTool}>
                <FontAwesomeIcon icon={faPlus} /> Add
              </button>
            </div>
            <div className="tags-container-comprehensive" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
              {formData.tools.map((tool, index) => (
                <div 
                  key={index} 
                  className="tag-comprehensive"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    backgroundColor: '#e7f3ff',
                    color: '#0a66c2',
                    borderRadius: '16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    border: '1px solid #d0e8ff',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#d0e8ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#e7f3ff';
                  }}
                >
                  {tool}
                  <span 
                    className="remove-comprehensive" 
                    onClick={() => removeTool(index)}
                    style={{
                      cursor: 'pointer',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: '#0a66c2',
                      lineHeight: '1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#0a66c2';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#0a66c2';
                    }}
                  >×</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Languages Section */}
        <div className="section-comprehensive">
          <h2 className="section-title-comprehensive" style={{ fontSize: '24px', fontWeight: 'bold', color: '#333', marginBottom: '8px' }}>
            Languages & Proficiency Levels <span className="required-comprehensive" style={{ color: '#e74c3c' }}>*</span>
          </h2>
          
          <div className="form-group-comprehensive">
            <div className="info-badge-comprehensive" style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px', 
              padding: '12px 16px', 
              backgroundColor: '#e7f3ff', 
              border: '1px solid #d0e8ff', 
              borderRadius: '8px', 
              marginBottom: '20px',
              color: '#0a66c2',
              fontSize: '14px'
            }}>
              <FontAwesomeIcon icon={faInfoCircle} /> 
              Language skills are crucial for international opportunities
            </div>
            
            <div className="language-proficiency-comprehensive" style={{ 
              display: 'flex', 
              gap: '12px', 
              marginBottom: '16px' 
            }}>
              <input 
                type="text" 
                value={languageInput.language}
                onChange={(e) => setLanguageInput(prev => ({ ...prev, language: e.target.value }))}
                placeholder="Enter language (e.g., English)"
                style={{
                  flex: '2',
                  padding: '12px 16px',
                  border: '2px solid #3498db',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.3s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#2980b9'}
                onBlur={(e) => e.target.style.borderColor = '#3498db'}
              />
              <select
                value={languageInput.proficiency}
                onChange={(e) => setLanguageInput(prev => ({ ...prev, proficiency: e.target.value }))}
                style={{
                  flex: '1',
                  padding: '12px 16px',
                  border: '2px solid #bdc3c7',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="">Select Level</option>
                <option value="native">Native/Bilingual</option>
                <option value="fluent">Fluent</option>
                <option value="advanced">Advanced</option>
                <option value="intermediate">Intermediate</option>
                <option value="basic">Basic</option>
              </select>
            </div>
            
            <button 
              type="button" 
              className="add-btn-comprehensive" 
              onClick={addLanguage} 
              style={{ 
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'background-color 0.3s ease',
                width: 'auto'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#2980b9'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#3498db'}
            >
              <FontAwesomeIcon icon={faPlus} /> Add Language
            </button>
            
            <div className="tags-container-comprehensive" style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '8px', 
              marginTop: '20px' 
            }}>
              {formData.languages.map((lang, index) => (
                <div 
                  key={index} 
                  className="tag-comprehensive"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    backgroundColor: '#e7f3ff',
                    color: '#0a66c2',
                    borderRadius: '16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    border: '1px solid #d0e8ff',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#d0e8ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#e7f3ff';
                  }}
                >
                  {lang.language} - {lang.proficiency}
                  <span 
                    className="remove-comprehensive" 
                    onClick={() => removeLanguage(index)}
                    style={{
                      cursor: 'pointer',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: '#0a66c2',
                      lineHeight: '1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#0a66c2';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#0a66c2';
                    }}
                  >×</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Certifications Section */}
        <div className="section-comprehensive">
          <h2 className="section-title-comprehensive">
            <FontAwesomeIcon icon={faCertificate} />
            Certifications & Licenses
          </h2>
          
          <div id="certificationContainer">
            {formData.certificationEntries.map((cert, index) => (
              <div key={index}>
                {index > 0 && (
                  <button
                    type="button"
                    className="remove-item-btn-comprehensive"
                    onClick={() => removeCertification(index)}
                    title="Remove this certification"
                    style={{ marginBottom: '15px' }}
                  >
                    ×
                  </button>
                )}
                
                <div className="form-row-comprehensive">
                  <div className="form-group-comprehensive">
                    <label>Certification/License Name</label>
                    <input 
                      type="text" 
                      placeholder="e.g., PMP, AWS Certified Solutions Architect"
                      value={cert.certificationName}
                      onChange={(e) => handleCertificationChange(index, 'certificationName', e.target.value)}
                    />
                  </div>
                  <div className="form-group-comprehensive">
                    <label>Issuing Organization</label>
                    <input 
                      type="text" 
                      placeholder="e.g., Project Management Institute"
                      value={cert.certIssuer}
                      onChange={(e) => handleCertificationChange(index, 'certIssuer', e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="form-row-comprehensive">
                  <div className="form-group-comprehensive">
                    <label>Issue Date</label>
                    <input 
                      type="month"
                      value={cert.certIssueDate}
                      onChange={(e) => handleCertificationChange(index, 'certIssueDate', e.target.value)}
                    />
                  </div>
                  <div className="form-group-comprehensive">
                    <label>Expiry Date</label>
                    <input 
                      type="month"
                      value={cert.certExpiryDate}
                      onChange={(e) => handleCertificationChange(index, 'certExpiryDate', e.target.value)}
                    />
                  </div>
                  <div className="form-group-comprehensive">
                    <label>Credential ID</label>
                    <input 
                      type="text" 
                      placeholder="Certificate number/ID"
                      value={cert.credentialId}
                      onChange={(e) => handleCertificationChange(index, 'credentialId', e.target.value)}
                    />
                  </div>
                </div>
                {index < formData.certificationEntries.length - 1 && (
                  <hr style={{ margin: '20px 0', border: '1px solid #e0e0e0' }} />
                )}
              </div>
            ))}
          </div>
          
          <button type="button" className="add-more-btn-comprehensive" onClick={addCertification}>
            <FontAwesomeIcon icon={faPlus} /> Add Another Certification
          </button>
        </div>

        {/* Professional Memberships Section */}
        <div className="section-comprehensive">
          <h2 className="section-title-comprehensive">
            <FontAwesomeIcon icon={faUsers} />
            Professional Memberships & Associations
          </h2>
          
          <div className="form-group-comprehensive">
            <label>Organization Name</label>
            <input 
              type="text" 
              name="membershipOrg" 
              placeholder="e.g., IEEE, PMI, Institute of Accountants"
              value={formData.membershipOrg}
              onChange={handleInputChange}
            />
          </div>
          
          <div className="form-row-comprehensive">
            <div className="form-group-comprehensive">
              <label>Membership Type</label>
              <input 
                type="text" 
                name="membershipType" 
                placeholder="e.g., Professional Member, Fellow"
                value={formData.membershipType}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group-comprehensive">
              <label>Membership Since</label>
              <input 
                type="month" 
                name="membershipDate"
                value={formData.membershipDate}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        {/* References Section */}
        <div className="section-comprehensive">
          <h2 className="section-title-comprehensive">
            <FontAwesomeIcon icon={faUserCheck} />
            Professional References
          </h2>
          
          <div id="referenceContainer">
            {formData.referenceEntries.map((reference, index) => (
              <div key={index} className="reference-item-comprehensive">
                {index > 0 && (
                  <button
                    type="button"
                    className="remove-item-btn-comprehensive"
                    onClick={() => removeReference(index)}
                    title="Remove this reference"
                  >
                    ×
                  </button>
                )}
                
                <div className="form-row-comprehensive">
                  <div className="form-group-comprehensive">
                    <label>Reference Name</label>
                    <input 
                      type="text" 
                      placeholder="Full name"
                      value={reference.referenceName}
                      onChange={(e) => handleReferenceChange(index, 'referenceName', e.target.value)}
                    />
                  </div>
                  <div className="form-group-comprehensive">
                    <label>Job Title</label>
                    <input 
                      type="text" 
                      placeholder="Position"
                      value={reference.referenceTitle}
                      onChange={(e) => handleReferenceChange(index, 'referenceTitle', e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="form-row-comprehensive">
                  <div className="form-group-comprehensive">
                    <label>Company/Organization</label>
                    <input 
                      type="text" 
                      placeholder="Company name"
                      value={reference.referenceCompany}
                      onChange={(e) => handleReferenceChange(index, 'referenceCompany', e.target.value)}
                    />
                  </div>
                  <div className="form-group-comprehensive">
                    <label>Relationship</label>
                    <input 
                      type="text" 
                      placeholder="e.g., Former Manager, Colleague"
                      value={reference.referenceRelationship}
                      onChange={(e) => handleReferenceChange(index, 'referenceRelationship', e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="form-row-comprehensive">
                  <div className="form-group-comprehensive">
                    <label>Email</label>
                    <input 
                      type="email" 
                      placeholder="email@example.com"
                      value={reference.referenceEmail}
                      onChange={(e) => handleReferenceChange(index, 'referenceEmail', e.target.value)}
                    />
                  </div>
                  <div className="form-group-comprehensive">
                    <label>Phone</label>
                    <input 
                      type="tel" 
                      placeholder="+1 234 567 8900"
                      value={reference.referencePhone}
                      onChange={(e) => handleReferenceChange(index, 'referencePhone', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <button type="button" className="add-more-btn-comprehensive" onClick={addReference}>
            <FontAwesomeIcon icon={faPlus} /> Add Another Reference
          </button>
        </div>

        {/* Professional Online Presence Section */}
        <div className="section-comprehensive">
          <h2 className="section-title-comprehensive">
            <FontAwesomeIcon icon={faLink} />
            Professional Online Presence
          </h2>
          <div className="info-badge-comprehensive">
            <FontAwesomeIcon icon={faInfoCircle} /> Add your professional online profiles and portfolio links
          </div>
          
          <div style={{ marginTop: '20px' }}>
            <div className="form-row-comprehensive">
              <div className="form-group-comprehensive">
                <label>Link Type</label>
                <select
                  value={linkInput.type}
                  onChange={(e) => setLinkInput(prev => ({ ...prev, type: e.target.value }))}
                >
                  <option value="">Select link type</option>
                  <option value="LinkedIn">LinkedIn</option>
                  <option value="GitHub">GitHub</option>
                  <option value="Personal Website">Personal Website/Portfolio</option>
                  <option value="Twitter/X">Twitter/X</option>
                  <option value="Behance">Behance</option>
                  <option value="Dribbble">Dribbble</option>
                  <option value="Medium">Medium</option>
                  <option value="Stack Overflow">Stack Overflow</option>
                  <option value="Other">Other Professional</option>
                </select>
              </div>
              <div className="form-group-comprehensive">
                <label>URL</label>
                <input 
                  type="url" 
                  value={linkInput.url}
                  onChange={(e) => setLinkInput(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://example.com/yourprofile"
                />
              </div>
            </div>
            <button type="button" className="add-btn-comprehensive" onClick={addLink} style={{ marginBottom: '20px' }}>
              <FontAwesomeIcon icon={faPlus} /> Add Link
            </button>
          </div>
          
          <div id="linksContainer" style={{ marginTop: '15px' }}>
            {Array.isArray(formData.professionalLinks) ? formData.professionalLinks.map((link, index) => (
              <div key={index} className="link-item-comprehensive">
                <div className="link-item-content-comprehensive">
                  <div className="link-item-type-comprehensive">
                    <FontAwesomeIcon icon={faLink} /> {link.type}
                  </div>
                  <div className="link-item-url-comprehensive">
                    <a href={link.url} target="_blank" rel="noopener noreferrer">{link.url}</a>
                  </div>
                </div>
                <button type="button" className="remove-item-btn-comprehensive" onClick={() => removeLink(index)}>×</button>
              </div>
            )) : null}
          </div>
        </div>

        {/* Job Preferences Section */}
        <div className="section-comprehensive">
          <h2 className="section-title-comprehensive">
            <FontAwesomeIcon icon={faSlidersH} />
            Job Preferences & Availability
          </h2>
          
          <div className="form-group-comprehensive">
            <label>Preferred Job Titles</label>
            <input 
              type="text" 
              name="preferredJobTitles" 
              placeholder="e.g., Software Engineer, Full Stack Developer, Backend Engineer"
              value={formData.preferredJobTitles}
              onChange={handleInputChange}
            />
            <small style={{ color: '#666', fontSize: '13px', marginTop: '5px', display: 'block' }}>
              Enter job titles you're interested in, separated by commas
            </small>
          </div>
          
          <div className="form-row-comprehensive">
            <div className="form-group-comprehensive">
              <label>Desired Job Type <span className="required-comprehensive">*</span></label>
              <select 
                name="jobType" 
                required
                value={formData.jobType}
                onChange={handleInputChange}
              >
                <option value="">Select job type</option>
                <option value="full-time">Full-time</option>
                <option value="part-time">Part-time</option>
                <option value="contract">Contract</option>
                <option value="freelance">Freelance</option>
                <option value="internship">Internship</option>
                <option value="temporary">Temporary</option>
              </select>
            </div>
            <div className="form-group-comprehensive">
              <label>Notice Period <span className="required-comprehensive">*</span></label>
              <select 
                name="noticePeriod" 
                required
                value={formData.noticePeriod}
                onChange={handleInputChange}
              >
                <option value="">Select notice period</option>
                <option value="immediate">Immediate/Available Now</option>
                <option value="1-week">1 Week</option>
                <option value="2-weeks">2 Weeks</option>
                <option value="1-month">1 Month</option>
                <option value="2-months">2 Months</option>
                <option value="3-months">3 Months</option>
                <option value="more">More than 3 Months</option>
              </select>
            </div>
          </div>

          <div className="form-row-comprehensive">
            <div className="form-group-comprehensive">
              <label>Current Salary (Optional)</label>
              <input 
                type="text" 
                name="currentSalary" 
                placeholder="e.g., USD 50,000 per year"
                value={formData.currentSalary}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group-comprehensive">
              <label>Expected Salary</label>
              <input 
                type="text" 
                name="expectedSalary" 
                placeholder="e.g., USD 60,000 - 70,000 per year"
                value={formData.expectedSalary}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-group-comprehensive">
            <label>Currency Preference</label>
            <select 
              name="currencyPreference"
              value={formData.currencyPreference}
              onChange={handleInputChange}
            >
              <option value="">Select currency</option>
              <option value="USD">USD - US Dollar</option>
              <option value="EUR">EUR - Euro</option>
              <option value="GBP">GBP - British Pound</option>
              <option value="KES">KES - Kenyan Shilling</option>
              <option value="ZAR">ZAR - South African Rand</option>
              <option value="NGN">NGN - Nigerian Naira</option>
              <option value="AED">AED - UAE Dirham</option>
              <option value="CAD">CAD - Canadian Dollar</option>
              <option value="AUD">AUD - Australian Dollar</option>
              <option value="INR">INR - Indian Rupee</option>
              <option value="other">Other</option>
            </select>
            {formData.currencyPreference === 'other' && (
              <input
                type="text"
                name="currencyPreferenceOther"
                placeholder="Please specify currency"
                value={formData.currencyPreferenceOther}
                onChange={handleInputChange}
                style={{ marginTop: '10px' }}
              />
            )}
          </div>

          <div className="form-group-comprehensive">
            <label>Travel Availability</label>
            <select 
              name="travelAvailability"
              value={formData.travelAvailability}
              onChange={handleInputChange}
            >
              <option value="">Select availability</option>
              <option value="no-travel">No Travel</option>
              <option value="minimal">Minimal (Less than 25%)</option>
              <option value="moderate">Moderate (25-50%)</option>
              <option value="frequent">Frequent (50-75%)</option>
              <option value="extensive">Extensive (More than 75%)</option>
            </select>
          </div>
        </div>

        {/* Additional Information Section */}
        <div className="section-comprehensive">
          <h2 className="section-title-comprehensive">
            <FontAwesomeIcon icon={faInfoCircle} />
            Additional Information
          </h2>

          <div className="section-content-comprehensive">
          <div className="form-group-comprehensive" style={{ width: '100%' }}>
            <label>Career Objectives</label>
            <textarea 
              name="careerObjectives" 
              placeholder="What are your short-term and long-term career goals? What do you hope to achieve in your next role?"
              value={formData.careerObjectives}
              onChange={handleInputChange}
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          </div>

          <div className="form-group-comprehensive" style={{ width: '100%' }}>
            <label>Hobbies & Interests</label>
            <textarea 
              name="hobbies" 
              placeholder="Share your interests outside of work (optional but helps employers understand you better)"
              value={formData.hobbies}
              onChange={handleInputChange}
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          </div>

          <div className="form-group-comprehensive" style={{ width: '100%' }}>
            <label>Additional Comments</label>
            <textarea 
              name="additionalComments" 
              placeholder="Any other information you'd like to share with potential employers"
              value={formData.additionalComments}
              onChange={handleInputChange}
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          </div>

          <div className="form-group-comprehensive" style={{ width: '100%' }}>
            <label>Would you like to participate in any volunteering?</label>
            <select
              name="participateInVolunteering"
              value={formData.participateInVolunteering}
              onChange={handleInputChange}
              style={{ width: '100%', boxSizing: 'border-box', padding: '12px', borderRadius: '8px', border: '1px solid #e5e7eb' }}
            >
              <option value="">Select an option</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          {formData.participateInVolunteering === 'yes' && (
            <div className="form-group-comprehensive" style={{ width: '100%' }}>
              <label>Volunteering Details</label>
              <textarea 
                name="volunteeringDetails" 
                placeholder="Please describe the volunteering activities you'd like to participate in"
                value={formData.volunteeringDetails}
                onChange={handleInputChange}
                rows="4"
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>
          )}

          <div className="checkbox-group-comprehensive">
            <input 
              type="checkbox" 
              id="agreeTerms" 
              name="agreeTerms" 
              required
              checked={formData.agreeTerms}
              onChange={handleInputChange}
            />
            <label htmlFor="agreeTerms">I agree to the Terms of Service and Privacy Policy <span className="required-comprehensive">*</span></label>
          </div>

          <div className="checkbox-group-comprehensive">
            <input 
              type="checkbox" 
              id="allowContact" 
              name="allowContact"
              checked={formData.allowContact}
              onChange={handleInputChange}
            />
            <label htmlFor="allowContact">I agree to be contacted by employers and recruiters</label>
          </div>

          <div className="checkbox-group-comprehensive">
            <input 
              type="checkbox" 
              id="accurateInfo" 
              name="accurateInfo" 
              required
              checked={formData.accurateInfo}
              onChange={handleInputChange}
            />
            <label htmlFor="accurateInfo">I confirm all information provided is accurate and truthful <span className="required-comprehensive">*</span></label>
          </div>
          </div>
        </div>

        {/* Submit Section */}
        <div className="submit-section-comprehensive">
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button 
              type="button" 
              onClick={handleGoToDashboard}
              style={{
                background: '#6b7280',
                color: 'white',
                border: 'none',
                padding: '14px 32px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#4b5563';
              }}
              onMouseOut={(e) => {
                e.target.style.background = '#6b7280';
              }}
            >
              <FontAwesomeIcon icon={faRocket} /> Go to dashboard
            </button>

            <button type="submit" className="submit-btn-comprehensive" disabled={isLoading}>
              {isLoading ? (
                <>
                  <FontAwesomeIcon icon={faRocket} spin /> Creating Profile...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheckCircle} /> Create Job Seeker Profile
                </>
              )}
            </button>
          </div>
          <p style={{ marginTop: '15px', color: '#666', fontSize: '14px', textAlign: 'center' }}>
            Use Complete profile in the header to save and go to dashboard, or Go to dashboard to skip
          </p>
        </div>
        </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default JobSeekerRegistrationFormComprehensive;

